/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Alert,
    Snackbar,
    Drawer,
    Popover,
    useMediaQuery,
    CircularProgress,
} from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import MainNav from "../components/layout/MainNav"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import DeleteIcon from "@mui/icons-material/Delete"
import SearchIcon from "@mui/icons-material/Search"
import PersonIcon from "@mui/icons-material/Person"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import LocalOfferIcon from "@mui/icons-material/LocalOffer"
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline"
import CreditCardIcon from "@mui/icons-material/CreditCard"
import ReceiptIcon from "@mui/icons-material/Receipt"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import NoteAddIcon from "@mui/icons-material/NoteAdd"
import SaveIcon from "@mui/icons-material/Save"
import ArchiveIcon from "@mui/icons-material/Archive"
import UndoIcon from "@mui/icons-material/Undo"
import CancelIcon from "@mui/icons-material/Cancel"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import PrintIcon from "@mui/icons-material/Print"
import EmailIcon from "@mui/icons-material/Email"
import QrCode2Icon from "@mui/icons-material/QrCode2"
import CloseIcon from "@mui/icons-material/Close"
import LockIcon from "@mui/icons-material/Lock"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"
import PaymentsIcon from "@mui/icons-material/Payments"
import PaymentIcon from "@mui/icons-material/Payment"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import apiClient from "../services/apiClient"
import salesService from "../services/salesService"
import paymentsService from "../services/paymentsService"
import cashSessionService from "../services/cashSessionService"
import customersService from "../services/customersService"
import documentsService from "../services/documentsService"
import QRCode from "qrcode"
import { useAuth } from "../services/AuthContext"
import CashFundCountDialog from "../components/CashFundCountDialog"
import { useTranslation } from "react-i18next"

/* ───────── helpers ───────── */
function formatMinor(minor: number | undefined | null, currency = "EUR") {
    const val = (Number(minor || 0) / 100).toFixed(2)
    return `${val} €`
}

function getLineUnitPriceTtcMinor(line: any): number {
    const unitPriceMinor = Number(line?.unit_price_minor || 0)
    const netMinor = Number(line?.net_minor || 0)
    const vatMinor = Number(line?.vat_minor || 0)
    const vatRatio =
        Number.isFinite(netMinor) && netMinor > 0 && Number.isFinite(vatMinor)
            ? vatMinor / netMinor
            : null

    if (
        Number.isFinite(unitPriceMinor) &&
        unitPriceMinor >= 0 &&
        vatRatio != null
    ) {
        return Math.round(unitPriceMinor * (1 + vatRatio))
    }

    const grossMinor = Number(line?.gross_minor || 0)
    const quantity = Number(line?.quantity || 0)

    if (
        Number.isFinite(quantity) &&
        quantity > 0 &&
        Number.isFinite(grossMinor)
    ) {
        return Math.round(grossMinor / quantity)
    }

    return unitPriceMinor
}

function getMovementTypeLabel(
    movement: any,
    t: (key: string, options?: Record<string, any>) => string
) {
    const movementType = String(movement?.movement_type || "").toLowerCase()
    const reasonCode = String(movement?.reason_code || "").toLowerCase()
    if (movementType === "comptage" || reasonCode === "comptage")
        return t("posPage.movements.types.counting")
    if (movementType === "fond_de_caisse" || reasonCode === "fond_de_caisse") {
        return t("posPage.movements.types.openingFund")
    }
    if (movementType === "ticket" || reasonCode === "ticket")
        return t("posPage.movements.types.ticket")
    if (movementType === "manual_in" || reasonCode === "entree")
        return t("posPage.movements.types.in")
    if (movementType === "manual_out" || reasonCode === "sortie")
        return t("posPage.movements.types.out")
    return movement?.reason_code || t("posPage.movements.types.default")
}

function isTicketMovement(movement: any) {
    const movementType = String(movement?.movement_type || "").toLowerCase()
    const reasonCode = String(movement?.reason_code || "").toLowerCase()
    return movementType === "ticket" || reasonCode === "ticket"
}

function getMovementCommentLabel(
    movement: any,
    t: (key: string, options?: Record<string, any>) => string
) {
    if (isTicketMovement(movement)) {
        const ticketId = String(
            movement?.ticket_ref_display ||
                movement?.ticket_ref ||
                movement?.ticketRef ||
                movement?.note ||
                ""
        ).trim()
        return ticketId || "—"
    }

    const raw = String(movement?.note || "").trim()
    if (!raw) {
        return t("common.dash")
    }

    return (
        raw.replace(/\s*·\s*Nb cheques:\s*\d+\s*$/i, "").trim() ||
        t("common.dash")
    )
}

function normalizePaymentMethodType(type: any): string {
    const normalized = String(type || "")
        .toLowerCase()
        .trim()
    if (normalized === "cheque") {
        return "check"
    }
    return normalized
}

function sortPaymentMethodsByDisplayOrder(methods: any[]): any[] {
    return [...(methods || [])].sort((a, b) => {
        const aOrder = Number.isFinite(Number(a?.display_order))
            ? Number(a.display_order)
            : Number.MAX_SAFE_INTEGER
        const bOrder = Number.isFinite(Number(b?.display_order))
            ? Number(b.display_order)
            : Number.MAX_SAFE_INTEGER

        if (aOrder !== bOrder) {
            return aOrder - bOrder
        }

        return String(a?.label || "").localeCompare(String(b?.label || ""))
    })
}

function hexToRgba(hex: string, alpha: number): string {
    const cleaned = String(hex || "").replace("#", "")
    const isShort = cleaned.length === 3
    const normalized =
        cleaned.length === 6
            ? cleaned
            : isShort
            ? cleaned
                  .split("")
                  .map((char) => char + char)
                  .join("")
            : "6b7280"

    const intVal = Number.parseInt(normalized, 16)
    const r = (intVal >> 16) & 255
    const g = (intVal >> 8) & 255
    const b = intVal & 255
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function getPaymentMethodTypeLabel(
    type: any,
    t: (key: string, options?: Record<string, any>) => string
) {
    const normalized = normalizePaymentMethodType(type)
    if (!normalized) return t("common.dash")
    if (normalized === "cash") return t("common.paymentMethods.cash")
    if (normalized === "check") return t("common.paymentMethods.check")
    if (normalized === "voucher") return t("common.paymentMethods.voucher")
    if (normalized === "card") return t("common.paymentMethods.card")
    if (normalized === "transfer") return t("common.paymentMethods.transfer")
    return normalized
}

function getPaymentMethodIcon(type: any, sx?: Record<string, any>) {
    const normalized = normalizePaymentMethodType(type)

    if (normalized === "cash") {
        return <PaymentsIcon sx={sx} />
    }
    if (normalized === "check") {
        return <ReceiptLongIcon sx={sx} />
    }
    if (normalized === "voucher") {
        return <LocalOfferIcon sx={sx} />
    }
    if (normalized === "transfer") {
        return <SwapHorizIcon sx={sx} />
    }

    if (normalized === "card") {
        return <CreditCardIcon sx={sx} />
    }

    return <PaymentIcon sx={sx} />
}

function getPaymentMethodBadgeStyle(type: any): React.CSSProperties {
    const normalized = normalizePaymentMethodType(type)

    const typeColors: Record<string, string> = {
        cash: "#16a34a",
        card: "#2563eb",
        check: "#d97706",
        voucher: "#7c3aed",
        transfer: "#0891b2",
        other: "#6b7280",
    }

    const color = typeColors[normalized] || typeColors.other

    return {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "999px",
        fontSize: "0.7rem",
        fontWeight: 700,
        backgroundColor: hexToRgba(color, 0.18),
        color,
        border: `1px solid ${hexToRgba(color, 0.35)}`,
    }
}

function getCustomerDisplayName(customer: any, fallback = "—"): string {
    if (!customer) {
        return fallback
    }

    const source = {
        ...(customer?.customer_snapshot || customer?.customerSnapshot || {}),
        ...customer,
    }

    const normalize = (value: any) => String(value || "").trim()
    const name =
        normalize(source?.name) ||
        normalize(source?.customer_name) ||
        normalize(source?.full_name)
    const companyName =
        normalize(source?.company_name) ||
        normalize(source?.raison_sociale) ||
        normalize(source?.raisonSociale) ||
        normalize(source?.business_name) ||
        normalize(source?.legal_name)
    const customerType = normalize(
        source?.type || source?.customer_type || source?.customerType
    ).toLowerCase()
    const isBusinessCustomer = [
        "business",
        "company",
        "entreprise",
        "societe",
        "société",
    ].includes(customerType)

    if (name) {
        return name
    }

    if (isBusinessCustomer && companyName) {
        return companyName
    }

    return companyName || fallback
}

function computeSessionAvailableFundMinor(
    movements: any[],
    openingFloatMinor: number
): number {
    const delta = (movements || []).reduce((acc, movement) => {
        const direction = String(movement?.direction || "").toLowerCase()
        const movementType = String(movement?.movement_type || "").toLowerCase()
        const reasonCode = String(movement?.reason_code || "").toLowerCase()
        const methodType = String(
            movement?.payment_method_type || ""
        ).toLowerCase()
        const amountMinor = Number(movement?.amount_minor || 0)

        const isManualIn =
            movementType === "manual_in" || reasonCode === "entree"
        const isManualOut =
            movementType === "manual_out" || reasonCode === "sortie"
        const isTicket = movementType === "ticket" || reasonCode === "ticket"

        if (!(isManualIn || isManualOut || isTicket)) {
            return acc
        }

        if (methodType && methodType !== "cash") {
            return acc
        }

        if (direction === "in") {
            return acc + amountMinor
        }
        if (direction === "out") {
            return acc - amountMinor
        }
        return acc
    }, 0)

    return Number(openingFloatMinor || 0) + delta
}

function parseChequeCountFromMovement(movement: any): number {
    const methodType = normalizePaymentMethodType(movement?.payment_method_type)
    if (methodType !== "check") {
        return 0
    }

    const movementType = String(movement?.movement_type || "").toLowerCase()
    const reasonCode = String(movement?.reason_code || "").toLowerCase()
    if (movementType === "ticket" || reasonCode === "ticket") {
        return 1
    }

    const note = String(movement?.note || "")
    const explicitCount = note.match(
        /(?:nb|nombre)\s*(?:de)?\s*ch[e\u00e8]ques?\s*[:=]\s*(\d+)/i
    )
    if (!explicitCount) {
        return 0
    }

    const parsed = Number.parseInt(explicitCount[1], 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

/* ───────── component ───────── */
const POS_DRAWER_WIDTH = 260
const MOVEMENTS_LOOKBACK_DAYS = 3
const MOVEMENTS_PAGE_SIZE = 80

function PosPage() {
    const { t, i18n } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const {
        user,
        posAutoCloseNotice,
        pushPosAutoCloseNotice,
        consumePosAutoCloseNotice,
    } = useAuth() as any
    const [navDrawerOpen, setNavDrawerOpen] = useState(false)
    const navState = location.state as {
        selectedCompanyId?: string
        selectedRegisterId?: string
        selectedOutletId?: string
    } | null

    /* ── context state ── */
    const [, setCompanies] = useState<any[]>([])
    const [outlets, setOutlets] = useState<any[]>([])
    const [allRegisters, setAllRegisters] = useState<any[]>([])
    const [registers, setRegisters] = useState<any[]>([])
    const [paymentMethodsByOutlet, setPaymentMethodsByOutlet] = useState<
        Record<string, any[]>
    >({})
    const [paymentMethods, setPaymentMethods] = useState<any[]>([])
    const [posPrechecks, setPosPrechecks] = useState<any>(null)
    const [activeSession, setActiveSession] = useState<any>(null)
    const [showInactivityDialog, setShowInactivityDialog] = useState(false)

    const [companyId, setCompanyId] = useState("")
    const [outletId, setOutletId] = useState("")
    const [registerId, setRegisterId] = useState("")

    const redirectToRegisterSelection = useCallback(
        (state?: any) => {
            navigate("/cash-register-selection", {
                replace: true,
                state,
            })
        },
        [navigate]
    )

    /* ── sale state ── */
    const [sale, setSale] = useState<any>(null)
    const [lines, setLines] = useState<any[]>([])
    const [payments, setPayments] = useState<any[]>([])
    const [isCartLocked, setIsCartLocked] = useState(false)

    /* ── UI state ── */
    const [loading, setLoading] = useState(false)
    const [bootstrapping, setBootstrapping] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [selectedLineId, setSelectedLineId] = useState<string | null>(null)
    const [displayValue, setDisplayValue] = useState("")
    const [displayDirty, setDisplayDirty] = useState(true)
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
        string | null
    >(null)
    const [searchValue, setSearchValue] = useState("")
    const [scanInputManualMode, setScanInputManualMode] = useState(false)

    /* ── modals ── */
    const [showSearchModal, setShowSearchModal] = useState(false)
    const [searchResults, setSearchResults] = useState<any[]>([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [searchPage, setSearchPage] = useState(0)
    const [searchTotal, setSearchTotal] = useState(0)
    const [highlightedSearchIndex, setHighlightedSearchIndex] = useState(0)
    const [successMsg, setSuccessMsg] = useState<string | null>(null)
    const [showDiscountModal, setShowDiscountModal] = useState<
        false | "line" | "global"
    >(false)
    const [discountValue, setDiscountValue] = useState("")
    const [discountType, setDiscountType] = useState<"percentage" | "amount">(
        "percentage"
    )

    const [showCommentModal, setShowCommentModal] = useState(false)
    const [commentDraft, setCommentDraft] = useState("")

    /* ── other payment methods modal ── */
    const [showOtherPayments, setShowOtherPayments] = useState(false)
    const [showFinalizeModal, setShowFinalizeModal] = useState(false)
    const [showPaymentOverlay, setShowPaymentOverlay] = useState(false)

    /* ── receipt actions ── */
    const [currentReceipt, setCurrentReceipt] = useState<any>(null)
    const [showEmailInputModal, setShowEmailInputModal] = useState(false)
    const [emailInput, setEmailInput] = useState("")
    const [emailSending, setEmailSending] = useState(false)
    const [emailSuccess, setEmailSuccess] = useState(false)
    const [showQrModal, setShowQrModal] = useState(false)
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)

    /* ── customer ── */
    const [selectedCustomer, setSelectedCustomer] = useState<any>(null)
    const [showCustomerModal, setShowCustomerModal] = useState(false)
    const [customerSearch, setCustomerSearch] = useState("")
    const [customerList, setCustomerList] = useState<any[]>([])

    /* ── caisse menu ── */
    const [caisseAnchorEl, setCaisseAnchorEl] = useState<HTMLElement | null>(
        null
    )

    /* ── ticket management modals ── */
    const [showNewTicketModal, setShowNewTicketModal] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [showTicketSearchModal, setShowTicketSearchModal] = useState(false)
    const [showOnHoldModal, setShowOnHoldModal] = useState(false)
    const [showPaymentHistoryModal, setShowPaymentHistoryModal] =
        useState(false)
    const [showReversePaymentModal, setShowReversePaymentModal] =
        useState(false)
    const [showMovementsModal, setShowMovementsModal] = useState(false)
    const [showAddMovementModal, setShowAddMovementModal] = useState(false)
    const [sessionMovements, setSessionMovements] = useState<any[]>([])
    const [registerMovements, setRegisterMovements] = useState<any[]>([])
    const [registerMovementsOffset, setRegisterMovementsOffset] = useState(0)
    const [registerMovementsHasMore, setRegisterMovementsHasMore] =
        useState(false)
    const [registerMovementsLoadingMore, setRegisterMovementsLoadingMore] =
        useState(false)
    const [registerMovementsStartDate, setRegisterMovementsStartDate] =
        useState("")
    const [sessionMovementsLoading, setSessionMovementsLoading] =
        useState(false)
    const [movementDirection, setMovementDirection] = useState<"in" | "out">(
        "in"
    )
    const [movementPaymentMethod, setMovementPaymentMethod] = useState<
        "cash" | "cheque"
    >("cash")
    const [movementAmountInput, setMovementAmountInput] = useState("")
    const [movementChequeCountInput, setMovementChequeCountInput] = useState("")
    const [movementCommentInput, setMovementCommentInput] = useState("")
    const [showCloseOptionsModal, setShowCloseOptionsModal] = useState(false)
    const [showManualCloseModal, setShowManualCloseModal] = useState(false)
    const [ticketList, setTicketList] = useState<any[]>([])
    const [allTickets, setAllTickets] = useState<any[]>([])
    const [ticketSearchQuery, setTicketSearchQuery] = useState("")
    const [onHoldCount, setOnHoldCount] = useState(0)
    const [showOpenSessionModal, setShowOpenSessionModal] = useState(false)
    const [openingCountedInput, setOpeningCountedInput] = useState("0.00")
    const searchInputRef = useRef<HTMLInputElement | null>(null)
    const searchModalInputRef = useRef<HTMLInputElement | null>(null)
    const commentModalInputRef = useRef<HTMLInputElement | null>(null)
    const ticketSearchModalInputRef = useRef<HTMLInputElement | null>(null)
    const scannerBufferRef = useRef("")
    const scannerLastKeyTsRef = useRef(0)
    const scannerFlushTimerRef = useRef<number | null>(null)
    const scannerSequenceActiveRef = useRef(false)
    const scannerCaptureInProgressRef = useRef(false)
    const handleAddByRefRef = useRef<((val: string) => void) | null>(null)
    const recoveryHintStorageKey = useMemo(
        () => `pos:recovery-hint:${user?.id || "anonymous"}`,
        [user?.id]
    )

    /* ── clock ── */
    const [now, setNow] = useState(new Date())
    const isTabletHeader = useMediaQuery("(max-width:1280px)")
    const isTabletScannerMode = useMediaQuery(
        "(max-width:1280px) and (pointer:coarse)"
    )
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000)
        return () => clearInterval(t)
    }, [])

    const currentDate = now.toLocaleDateString(i18n.language || "fr-FR", {
        day: isTabletHeader ? "2-digit" : "numeric",
        month: isTabletHeader ? "2-digit" : "long",
        year: isTabletHeader ? "2-digit" : "numeric",
    })
    const currentTime = now.toLocaleTimeString(i18n.language || "fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        second: isTabletHeader ? undefined : "2-digit",
    })

    /* ── derived ── */
    const totalPaidMinor = useMemo(
        () => payments.reduce((acc, p) => acc + Number(p.amount_minor || 0), 0),
        [payments]
    )
    const grossTotalMinor = Number(sale?.gross_total_minor || 0)
    const netTotalMinor = Number(sale?.net_total_minor || 0)
    const vatTotalMinor = Number(sale?.vat_total_minor || 0)
    const subTotalMinor = useMemo(
        () => lines.reduce((acc, l) => acc + Number(l.gross_minor || 0), 0),
        [lines]
    )
    const globalDiscountAmountMinor = Number(
        sale?.global_discount_amount_minor || 0
    )
    const remainingMinor = useMemo(
        () => Math.max(0, grossTotalMinor - totalPaidMinor),
        [grossTotalMinor, totalPaidMinor]
    )
    const enteredAmountMinor = useMemo(() => {
        const normalized = String(displayValue || "").replace(",", ".")
        const parsed = Number.parseFloat(normalized)
        if (!Number.isFinite(parsed) || parsed <= 0) {
            return 0
        }
        return Math.round(parsed * 100)
    }, [displayValue])
    const selectedPaymentMethod = useMemo(
        () =>
            paymentMethods.find(
                (method) => method.id === selectedPaymentMethodId
            ) || null,
        [paymentMethods, selectedPaymentMethodId]
    )
    const selectedPaymentMethodType = String(
        selectedPaymentMethod?.type || selectedPaymentMethod?.payment_type || ""
    ).toLowerCase()
    const isCashPaymentSelected = selectedPaymentMethodType === "cash"
    const previewCapturedMinor = useMemo(() => {
        if (!enteredAmountMinor) {
            return 0
        }
        if (isCashPaymentSelected) {
            return Math.min(enteredAmountMinor, remainingMinor)
        }
        return enteredAmountMinor
    }, [enteredAmountMinor, isCashPaymentSelected, remainingMinor])
    const previewChangeMinor = useMemo(() => {
        if (!isCashPaymentSelected || !enteredAmountMinor) {
            return 0
        }
        return Math.max(0, enteredAmountMinor - previewCapturedMinor)
    }, [isCashPaymentSelected, enteredAmountMinor, previewCapturedMinor])
    const overRemainingForNonCash =
        enteredAmountMinor > remainingMinor &&
        selectedPaymentMethodType !== "" &&
        !isCashPaymentSelected
    const canSubmitPayment =
        !!selectedPaymentMethodId &&
        enteredAmountMinor > 0 &&
        !overRemainingForNonCash

    const isTotalized = Boolean(sale?.totalized_at)
    const canSelectRegister = !!companyId && !!outletId && !!registerId
    const canOperate = canSelectRegister && !!activeSession
    const canCollectPayment =
        canOperate && (isTotalized || isCartLocked || totalPaidMinor > 0)
    const lockErrorMsg = String(t("posPage.errors.cartLocked"))

    const registerName =
        registers.find((r) => r.id === registerId)?.name ||
        String(t("common.dash"))
    const outletName =
        outlets.find((o) => o.id === outletId)?.name ||
        activeSession?.outlet_name ||
        activeSession?.outletName ||
        String(t("common.dash"))
    const selectedCustomerDisplayName = selectedCustomer
        ? getCustomerDisplayName(
              selectedCustomer,
              String(t("posPage.header.noCustomer"))
          )
        : String(t("posPage.header.noCustomer"))
    const availableFundMinor = useMemo(
        () =>
            computeSessionAvailableFundMinor(
                sessionMovements,
                Number(activeSession?.opening_float_minor || 0)
            ),
        [sessionMovements, activeSession?.opening_float_minor]
    )
    const movementAmountMinor = useMemo(() => {
        const parsed = Number.parseFloat(movementAmountInput || "0")
        if (!Number.isFinite(parsed) || parsed <= 0) {
            return 0
        }
        return Math.round(parsed * 100)
    }, [movementAmountInput])
    const movementChequeCount = useMemo(() => {
        const parsed = Number.parseInt(movementChequeCountInput || "0", 10)
        if (!Number.isFinite(parsed) || parsed <= 0) {
            return 0
        }
        return parsed
    }, [movementChequeCountInput])
    const availableChequeMinor = useMemo(
        () =>
            (sessionMovements || []).reduce((acc, movement) => {
                const direction = String(
                    movement?.direction || ""
                ).toLowerCase()
                const movementType = String(
                    movement?.movement_type || ""
                ).toLowerCase()
                const reasonCode = String(
                    movement?.reason_code || ""
                ).toLowerCase()
                const methodType = String(
                    movement?.payment_method_type || ""
                ).toLowerCase()
                const amountMinor = Number(movement?.amount_minor || 0)

                const isManualIn =
                    movementType === "manual_in" || reasonCode === "entree"
                const isManualOut =
                    movementType === "manual_out" || reasonCode === "sortie"
                const isTicket =
                    movementType === "ticket" || reasonCode === "ticket"

                if (!(isManualIn || isManualOut || isTicket)) {
                    return acc
                }
                if (normalizePaymentMethodType(methodType) !== "check") {
                    return acc
                }
                if (direction === "in") {
                    return acc + amountMinor
                }
                if (direction === "out") {
                    return acc - amountMinor
                }
                return acc
            }, 0),
        [sessionMovements]
    )
    const availableChequeCount = useMemo(
        () =>
            (sessionMovements || []).reduce((acc, movement) => {
                const direction = String(
                    movement?.direction || ""
                ).toLowerCase()
                const movementType = String(
                    movement?.movement_type || ""
                ).toLowerCase()
                const reasonCode = String(
                    movement?.reason_code || ""
                ).toLowerCase()
                const isManualIn =
                    movementType === "manual_in" || reasonCode === "entree"
                const isManualOut =
                    movementType === "manual_out" || reasonCode === "sortie"
                const isTicket =
                    movementType === "ticket" || reasonCode === "ticket"
                if (!(isManualIn || isManualOut || isTicket)) {
                    return acc
                }

                const chequeCount = parseChequeCountFromMovement(movement)
                if (chequeCount <= 0) {
                    return acc
                }
                if (direction === "in") {
                    return acc + chequeCount
                }
                if (direction === "out") {
                    return acc - chequeCount
                }
                return acc
            }, 0),
        [sessionMovements]
    )
    const selectedAvailableMinor =
        movementPaymentMethod === "cheque"
            ? availableChequeMinor
            : availableFundMinor
    const movementAfterMinor = useMemo(() => {
        if (movementAmountMinor <= 0) {
            return selectedAvailableMinor
        }
        return movementDirection === "in"
            ? selectedAvailableMinor + movementAmountMinor
            : selectedAvailableMinor - movementAmountMinor
    }, [movementDirection, movementAmountMinor, selectedAvailableMinor])
    const movementAfterChequeCount = useMemo(() => {
        if (movementPaymentMethod !== "cheque" || movementChequeCount <= 0) {
            return availableChequeCount
        }
        return movementDirection === "in"
            ? availableChequeCount + movementChequeCount
            : availableChequeCount - movementChequeCount
    }, [
        movementDirection,
        movementPaymentMethod,
        movementChequeCount,
        availableChequeCount,
    ])
    const groupedMovementsBySession = useMemo(() => {
        const sorted = [...registerMovements].sort(
            (a, b) =>
                new Date(b.created_at || 0).getTime() -
                new Date(a.created_at || 0).getTime()
        )

        const groups = new Map<string, any[]>()
        sorted.forEach((movement) => {
            const sessionKey = movement.cash_session_id || "sans-session"
            if (!groups.has(sessionKey)) {
                groups.set(sessionKey, [])
            }
            groups.get(sessionKey)!.push(movement)
        })

        return Array.from(groups.entries()).map(([sessionId, movements]) => ({
            sessionId,
            movements,
        }))
    }, [registerMovements])
    const buildMovementsStartDate = useCallback(() => {
        const start = new Date(
            Date.now() - MOVEMENTS_LOOKBACK_DAYS * 24 * 60 * 60 * 1000
        )
        return start.toISOString()
    }, [])

    const refreshOnHoldCount = useCallback(async () => {
        if (!companyId || !registerId) {
            setOnHoldCount(0)
            return
        }

        try {
            const res = await salesService.countSales(
                companyId,
                registerId,
                "on_hold"
            )
            const nextCount = Number(res?.count ?? 0)
            setOnHoldCount(
                Number.isFinite(nextCount) && nextCount >= 0 ? nextCount : 0
            )
        } catch {
            setOnHoldCount(0)
        }
    }, [companyId, registerId])

    useEffect(() => {
        refreshOnHoldCount()
    }, [refreshOnHoldCount])

    /* ── data loading ── */
    useEffect(() => {
        let c = false
        ;(async () => {
            try {
                const res = await apiClient.get("/api/companies")
                if (c) return
                const list = res.companies || []
                setCompanies(list)
                if (list.length > 0) {
                    const hintedCompanyId =
                        navState?.selectedCompanyId &&
                        list.some(
                            (company: any) =>
                                company.id === navState.selectedCompanyId
                        )
                            ? navState.selectedCompanyId
                            : list[0].id
                    setCompanyId(hintedCompanyId)
                } else {
                    setBootstrapping(false)
                }
            } catch (e: any) {
                if (!c) {
                    setError(e?.userMessage || e.message)
                    setBootstrapping(false)
                }
            }
        })()
        return () => {
            c = true
        }
    }, [navState?.selectedCompanyId])

    useEffect(() => {
        if (!posAutoCloseNotice) {
            return
        }

        setSuccessMsg(posAutoCloseNotice)
        if (typeof consumePosAutoCloseNotice === "function") {
            consumePosAutoCloseNotice()
        }
    }, [posAutoCloseNotice, consumePosAutoCloseNotice])

    const clearPos = useCallback(() => {
        setSale(null)
        setLines([])
        setPayments([])
        setIsCartLocked(false)
        setSelectedLineId(null)
        setDisplayValue("")
        setSelectedPaymentMethodId(null)
        setSelectedCustomer(null)
        setCurrentReceipt(null)
    }, [])

    // Poll: detect if the active session was auto-closed by the backend worker
    useEffect(() => {
        if (!activeSession?.id || !companyId) return

        const interval = setInterval(async () => {
            try {
                const session = await cashSessionService.getSession(
                    companyId,
                    activeSession.id
                )
                if (session && (session as any).status === "closed") {
                    clearInterval(interval)
                    setActiveSession(null)
                    setShowInactivityDialog(true)
                    clearPos()
                    setAllRegisters((prev) =>
                        prev.map((r: any) =>
                            r.id === registerId ? { ...r, status: "free" } : r
                        )
                    )
                }
            } catch {
                // ignore transient network errors
            }
        }, 60_000)

        return () => clearInterval(interval)
    }, [activeSession?.id, companyId, registerId, clearPos])

    useEffect(() => {
        if (!recoveryHintStorageKey) return

        if (!activeSession?.id || !companyId) {
            try {
                localStorage.removeItem(recoveryHintStorageKey)
            } catch {}
            return
        }

        try {
            localStorage.setItem(
                recoveryHintStorageKey,
                JSON.stringify({
                    sessionId: activeSession.id,
                    companyId,
                    outletId: activeSession.outlet_id || outletId || null,
                    registerId: activeSession.register_id || registerId || null,
                    updatedAt: new Date().toISOString(),
                })
            )
        } catch {}
    }, [
        recoveryHintStorageKey,
        activeSession?.id,
        activeSession?.outlet_id,
        activeSession?.register_id,
        companyId,
        outletId,
        registerId,
    ])

    useEffect(() => {
        let c = false
        if (!companyId) {
            setOutlets([])
            setAllRegisters([])
            setRegisters([])
            setPaymentMethodsByOutlet({})
            setPaymentMethods([])
            setPosPrechecks(null)
            setActiveSession(null)
            setOutletId("")
            setRegisterId("")
            return
        }
        ;(async () => {
            try {
                const res = await cashSessionService.getPosContext(companyId)
                if (c) return

                const outletList = res.outlets || []
                const registerList = res.registers || []
                const methodsByOutlet = res.paymentMethodsByOutlet || {}

                setOutlets(outletList)
                setAllRegisters(registerList)
                setPaymentMethodsByOutlet(methodsByOutlet)
                setPosPrechecks(res.prechecks || null)
                setActiveSession(res.activeSession || null)

                const autoCloseMessage =
                    res.autoCloseNotification && !res.activeSession?.id
                        ? "Votre session de caisse a été automatiquement clôturée pour inactivité."
                        : null
                if (
                    autoCloseMessage &&
                    typeof pushPosAutoCloseNotice === "function"
                ) {
                    pushPosAutoCloseNotice(autoCloseMessage)
                }

                if (!res.activeSession?.id) {
                    setShowOpenSessionModal(false)
                    setError(null)
                    redirectToRegisterSelection({
                        selectedCompanyId: companyId || undefined,
                    })
                    return
                }

                setBootstrapping(false)

                const restoreHints = res.autoRestore || null
                let persistedHint: any = null
                try {
                    const raw = localStorage.getItem(recoveryHintStorageKey)
                    persistedHint = raw ? JSON.parse(raw) : null
                } catch {
                    persistedHint = null
                }

                const canUsePersistedHint =
                    persistedHint &&
                    persistedHint.companyId === companyId &&
                    persistedHint.sessionId === res.activeSession?.id

                const hintedOutletId =
                    restoreHints?.outletId ||
                    (canUsePersistedHint &&
                    outletList.some(
                        (o: any) => o.id === persistedHint?.outletId
                    )
                        ? persistedHint.outletId
                        : null) ||
                    res.activeSession?.outlet_id ||
                    (navState?.selectedOutletId &&
                    outletList.some(
                        (o: any) => o.id === navState.selectedOutletId
                    )
                        ? navState.selectedOutletId
                        : null) ||
                    res.autoSelectHints?.outletId ||
                    outletList[0]?.id ||
                    ""

                setOutletId(hintedOutletId)

                const scopedRegisters = registerList.filter(
                    (r: any) => (r.outlet_id || r.outletId) === hintedOutletId
                )

                const hintedRegisterId =
                    restoreHints?.registerId ||
                    (canUsePersistedHint &&
                    scopedRegisters.some(
                        (r: any) => r.id === persistedHint?.registerId
                    )
                        ? persistedHint.registerId
                        : null) ||
                    res.activeSession?.register_id ||
                    (navState?.selectedRegisterId &&
                    scopedRegisters.some(
                        (r: any) => r.id === navState.selectedRegisterId
                    )
                        ? navState.selectedRegisterId
                        : null) ||
                    res.autoSelectHints?.registerId ||
                    scopedRegisters.find((r: any) => r.status === "free")?.id ||
                    scopedRegisters[0]?.id ||
                    ""

                setRegisterId(hintedRegisterId)

                const recoverableSaleId =
                    restoreHints?.saleId || res.recoverableSale?.id
                if (recoverableSaleId) {
                    const recovered = await salesService.getSale(
                        companyId,
                        recoverableSaleId
                    )
                    if (c) return

                    let recoverableSale = recovered.sale || res.recoverableSale
                    if (
                        recoverableSale?.id &&
                        String(recoverableSale?.status || "").toLowerCase() !==
                            "open"
                    ) {
                        try {
                            const reopened =
                                await salesService.updateSaleStatus(
                                    companyId,
                                    recoverableSale.id,
                                    "open"
                                )
                            recoverableSale = {
                                ...recoverableSale,
                                ...(reopened.sale || {}),
                                status: "open",
                            }
                        } catch {
                            recoverableSale = {
                                ...recoverableSale,
                                status: "open",
                            }
                        }
                    }

                    if (c) return
                    setSale(recoverableSale)
                    await hydrateSelectedCustomerFromSale(recoverableSale)
                    setLines(recovered.lines || [])
                    const existingPayments = recovered.payments || []
                    setPayments(existingPayments)
                    setIsCartLocked(
                        Boolean(recoverableSale?.cart_locked_at) ||
                            existingPayments.length > 0
                    )
                } else {
                    setSale(null)
                    setSelectedCustomer(null)
                    setLines([])
                    setPayments([])
                    setIsCartLocked(false)
                }
            } catch (e: any) {
                if (!c) {
                    setError(e?.userMessage || e.message)
                    setBootstrapping(false)
                }
            }
        })()
        return () => {
            c = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        companyId,
        redirectToRegisterSelection,
        pushPosAutoCloseNotice,
        recoveryHintStorageKey,
    ])

    useEffect(() => {
        if (!outletId) {
            setRegisters([])
            setRegisterId("")
            return
        }
        const list = allRegisters.filter(
            (r: any) => (r.outlet_id || r.outletId) === outletId
        )
        setRegisters(list)

        if (!list.some((r: any) => r.id === registerId)) {
            setRegisterId(
                list.find((r: any) => r.status === "free")?.id ||
                    list[0]?.id ||
                    ""
            )
        }
    }, [outletId, allRegisters, registerId])

    useEffect(() => {
        if (!companyId || !outletId) {
            setPaymentMethods([])
            return
        }
        setPaymentMethods(
            sortPaymentMethodsByDisplayOrder(
                paymentMethodsByOutlet[outletId] || []
            )
        )
    }, [companyId, outletId, paymentMethodsByOutlet])

    useEffect(() => {
        let cancelled = false
        if (!canSelectRegister) {
            setActiveSession(null)
            setShowOpenSessionModal(false)
            return
        }

        if (
            activeSession?.opened_by_user_id === user?.id &&
            activeSession?.register_id === registerId
        ) {
            setShowOpenSessionModal(false)
            setError(null)
            return
        }

        ;(async () => {
            try {
                const selectedRegister = allRegisters.find(
                    (r: any) => r.id === registerId
                )
                const session = await cashSessionService.getOpenSession(
                    companyId,
                    registerId
                )
                if (cancelled) return

                if (session?.opened_by_user_id === user?.id) {
                    setActiveSession(session || null)
                    setShowOpenSessionModal(false)
                    setError(null)
                    return
                }

                setActiveSession(null)
                setShowOpenSessionModal(false)
                if (
                    selectedRegister?.status === "used" &&
                    posPrechecks?.hasFreeRegister !== false
                ) {
                    setError(String(t("posPage.errors.registerAlreadyUsed")))
                }
                redirectToRegisterSelection({
                    selectedCompanyId: companyId || undefined,
                    selectedOutletId: outletId || undefined,
                    selectedRegisterId: registerId || undefined,
                })
            } catch (e: any) {
                if (cancelled) return
                if (e?.statusCode === 404) {
                    setActiveSession(null)
                    setShowOpenSessionModal(false)
                    setError(null)
                    redirectToRegisterSelection({
                        selectedCompanyId: companyId || undefined,
                        selectedOutletId: outletId || undefined,
                        selectedRegisterId: registerId || undefined,
                    })
                    return
                }
                setError(e?.userMessage || e.message)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [
        canSelectRegister,
        companyId,
        outletId,
        registerId,
        allRegisters,
        user?.id,
        activeSession,
        posPrechecks,
        redirectToRegisterSelection,
        t,
    ])

    useEffect(() => {
        if (!canOperate || showSearchModal) return
        setScanInputManualMode(false)
        const inputEl = searchInputRef.current
        if (!inputEl) return
        try {
            inputEl.focus({ preventScroll: true })
        } catch {
            inputEl.focus()
        }
    }, [canOperate, showSearchModal])

    /* ── sale helpers ── */
    const ensureSale = useCallback(async () => {
        if (sale) return sale
        if (!companyId || !outletId || !registerId)
            throw new Error(
                String(t("posPage.errors.selectCompanyOutletRegister"))
            )
        if (!activeSession) {
            throw new Error(
                String(t("posPage.errors.openSessionBeforeContinue"))
            )
        }
        const res = await salesService.createSale(companyId, {
            outletId,
            registerId,
            cashSessionId: activeSession.id,
            currency: "EUR",
            customerId: selectedCustomer?.id || null,
        })
        setSale(res.sale)
        setLines([])
        setPayments([])
        return res.sale
    }, [
        sale,
        companyId,
        outletId,
        registerId,
        activeSession,
        selectedCustomer,
        t,
    ])

    const handleNewSale = useCallback(() => {
        const hasOngoingTicket =
            Boolean(sale) || lines.length > 0 || payments.length > 0
        if (!hasOngoingTicket) {
            clearPos()
            return
        }
        setShowNewTicketModal(true)
    }, [sale, lines.length, payments.length, clearPos])

    const handleOpenSession = useCallback(async () => {
        if (!canSelectRegister) return

        const counted = Number.parseFloat(openingCountedInput || "0")
        if (Number.isNaN(counted) || counted < 0) {
            setError(String(t("posPage.errors.openingFundPositiveOrZero")))
            return
        }

        setError(null)
        setLoading(true)
        try {
            const session = await cashSessionService.openSession(companyId, {
                outletId,
                registerId,
                openingFloatMinor: Math.round(counted * 100),
                currency: "EUR",
            })
            setActiveSession(session)
            setShowOpenSessionModal(false)
            setSuccessMsg(String(t("posPage.messages.sessionOpened")))
        } catch (e: any) {
            setError(e?.userMessage || e.message)
        } finally {
            setLoading(false)
        }
    }, [
        canSelectRegister,
        openingCountedInput,
        companyId,
        outletId,
        registerId,
        t,
    ])

    const handleAddItem = useCallback(
        async (item: any) => {
            if (isCartLocked) {
                setError(lockErrorMsg)
                return
            }
            setError(null)
            setLoading(true)
            try {
                const s = await ensureSale()
                const res = await salesService.addSaleLine(companyId, s.id, {
                    itemId: item.id,
                    quantity: 1,
                })
                setSale(res.sale)
                setLines((prev) => {
                    const idx = prev.findIndex((l) => l.id === res.line.id)
                    if (idx >= 0) {
                        const updated = [...prev]
                        updated[idx] = res.line
                        return updated
                    }
                    return [...prev, res.line]
                })
            } catch (e: any) {
                setError(e?.userMessage || e.message)
            } finally {
                setLoading(false)
            }
        },
        [companyId, ensureSale, isCartLocked, lockErrorMsg]
    )

    const handleRemoveLine = useCallback(
        async (lineId: string) => {
            if (isCartLocked) {
                setError(lockErrorMsg)
                return
            }
            if (!sale) return
            setError(null)
            setLoading(true)
            try {
                const res = await salesService.removeSaleLine(
                    companyId,
                    sale.id,
                    lineId
                )
                setSale(res.sale)
                setLines((prev) => prev.filter((l) => l.id !== lineId))
            } catch (e: any) {
                setError(e?.userMessage || e.message)
            } finally {
                setLoading(false)
            }
        },
        [companyId, sale, isCartLocked, lockErrorMsg]
    )

    const handleUpdateQuantity = useCallback(
        async (lineId: string, newQuantity: number) => {
            if (isCartLocked) {
                setError(lockErrorMsg)
                return
            }
            if (!sale) return
            if (newQuantity <= 0) {
                return handleRemoveLine(lineId)
            }
            setError(null)
            setLoading(true)
            try {
                const res = await salesService.updateSaleLineQuantity(
                    companyId,
                    sale.id,
                    lineId,
                    newQuantity
                )
                setSale(res.sale)
                if (res.line) {
                    setLines((prev) =>
                        prev.map((l) => (l.id === lineId ? res.line : l))
                    )
                } else {
                    setLines((prev) => prev.filter((l) => l.id !== lineId))
                }
            } catch (e: any) {
                setError(e?.userMessage || e.message)
            } finally {
                setLoading(false)
            }
        },
        [companyId, sale, isCartLocked, handleRemoveLine, lockErrorMsg]
    )

    const handleApplyLineDiscount = useCallback(
        async (
            lineId: string,
            type: "percentage" | "amount",
            value: number
        ) => {
            if (isCartLocked) {
                setError(lockErrorMsg)
                return
            }
            if (!sale) return
            setError(null)
            setLoading(true)
            try {
                const res = await salesService.applyLineDiscount(
                    companyId,
                    sale.id,
                    lineId,
                    type,
                    value
                )
                setSale(res.sale)
                if (res.line) {
                    setLines((prev) =>
                        prev.map((l) => (l.id === lineId ? res.line : l))
                    )
                }
            } catch (e: any) {
                setError(e?.userMessage || e.message)
            } finally {
                setLoading(false)
                setShowDiscountModal(false)
            }
        },
        [companyId, sale, isCartLocked, lockErrorMsg]
    )

    const handleApplyGlobalDiscount = useCallback(
        async (type: "percentage" | "amount", value: number) => {
            if (isCartLocked) {
                setError(lockErrorMsg)
                return
            }
            if (!sale) return
            setError(null)
            setLoading(true)
            try {
                const res = await salesService.applyGlobalDiscount(
                    companyId,
                    sale.id,
                    type,
                    value
                )
                setSale(res.sale)
            } catch (e: any) {
                setError(e?.userMessage || e.message)
            } finally {
                setLoading(false)
                setShowDiscountModal(false)
            }
        },
        [companyId, sale, isCartLocked, lockErrorMsg]
    )

    const handleUpdateComment = useCallback(async () => {
        if (!sale) return
        setError(null)
        setLoading(true)
        try {
            const res = await salesService.updateSaleComment(
                companyId,
                sale.id,
                commentDraft
            )
            setSale(res.sale)
        } catch (e: any) {
            setError(e?.userMessage || e.message)
        } finally {
            setLoading(false)
            setShowCommentModal(false)
        }
    }, [companyId, sale, commentDraft])

    const handleSaveTicket = useCallback(async () => {
        if (!sale) return
        setLoading(true)
        try {
            await salesService.updateSaleStatus(companyId, sale.id, "saved")
            setSuccessMsg(String(t("posPage.messages.ticketSaved")))
            clearPos()
        } catch (e: any) {
            setError(e?.userMessage || e.message)
        } finally {
            setLoading(false)
            setShowNewTicketModal(false)
        }
    }, [companyId, sale, clearPos, t])

    const handleHoldTicket = useCallback(async () => {
        if (!sale) return
        setLoading(true)
        try {
            await salesService.updateSaleStatus(companyId, sale.id, "on_hold")
            clearPos()
            refreshOnHoldCount()
        } catch (e: any) {
            setError(e?.userMessage || e.message)
        } finally {
            setLoading(false)
        }
    }, [companyId, sale, refreshOnHoldCount, clearPos])

    const handleCancelTicket = useCallback(async () => {
        if (!sale) return
        setLoading(true)
        try {
            await salesService.cancelSale(companyId, sale.id)
            clearPos()
        } catch (e: any) {
            setError(e?.userMessage || e.message)
        } finally {
            setLoading(false)
            setShowCancelModal(false)
        }
    }, [companyId, sale, clearPos])

    const handleOpenCustomerModal = useCallback(async () => {
        setShowCustomerModal(true)
        setCustomerSearch("")

        if (!companyId) {
            setCustomerList([])
            return
        }

        try {
            const res = await customersService.listCustomers(companyId, {
                limit: 20,
                offset: 0,
            })
            setCustomerList(res.customers || [])
        } catch (e: any) {
            setError(e?.userMessage || e.message)
            setCustomerList([])
        }
    }, [companyId])

    const handleSearchCustomers = useCallback(
        async (query: string) => {
            setCustomerSearch(query)

            if (!companyId) {
                setCustomerList([])
                return
            }

            try {
                const res = await customersService.listCustomers(companyId, {
                    search: query,
                    limit: 20,
                    offset: 0,
                })
                setCustomerList(res.customers || [])
            } catch (e: any) {
                setError(e?.userMessage || e.message)
                setCustomerList([])
            }
        },
        [companyId]
    )

    async function hydrateSelectedCustomerFromSale(saleData: any) {
        const customerIdFromSale = String(saleData?.customer_id || "").trim()
        if (!customerIdFromSale || !companyId) {
            setSelectedCustomer(null)
            return
        }

        const customerLabel = getCustomerDisplayName(saleData, "")
        const fallbackCustomer = customerLabel
            ? {
                  id: customerIdFromSale,
                  name: saleData?.customer_name || saleData?.name || null,
                  company_name:
                      saleData?.company_name ||
                      saleData?.customer_company_name ||
                      null,
                  type: saleData?.customer_type || saleData?.type || null,
              }
            : { id: customerIdFromSale }

        try {
            const customer = await customersService.getCustomer(
                companyId,
                customerIdFromSale
            )
            setSelectedCustomer(customer || fallbackCustomer)
        } catch {
            setSelectedCustomer(fallbackCustomer)
        }
    }

    const handleSelectCustomer = useCallback(
        async (customer: any) => {
            setSelectedCustomer(customer)

            if (!companyId || !sale?.id) {
                return
            }

            try {
                const res = await salesService.updateSaleCustomer(
                    companyId,
                    sale.id,
                    customer?.id || null
                )
                if (res?.sale) {
                    setSale(res.sale)
                }
                if (Array.isArray(res?.lines)) {
                    setLines(res.lines)
                }
            } catch (e: any) {
                setError(e?.userMessage || e.message)
            }
        },
        [companyId, sale?.id]
    )

    const handleClearSelectedCustomer = useCallback(async () => {
        setSelectedCustomer(null)

        if (!companyId || !sale?.id) {
            return
        }

        try {
            const res = await salesService.updateSaleCustomer(
                companyId,
                sale.id,
                null
            )
            if (res?.sale) {
                setSale(res.sale)
            }
            if (Array.isArray(res?.lines)) {
                setLines(res.lines)
            }
        } catch (e: any) {
            setError(e?.userMessage || e.message)
        }
    }, [companyId, sale?.id])

    const handleLoadTicket = useCallback(
        async (saleId: string) => {
            setLoading(true)
            try {
                const res = await salesService.getSale(companyId, saleId)
                await salesService.updateSaleStatus(companyId, saleId, "open")
                setSale({ ...res.sale, status: "open" })
                await hydrateSelectedCustomerFromSale(res.sale)
                setLines(res.lines || [])
                const existingPayments = res.payments || []
                setPayments(existingPayments)
                setIsCartLocked(
                    Boolean(res.sale?.cart_locked_at) ||
                        existingPayments.length > 0
                )
                setSelectedPaymentMethodId(null)
                setDisplayValue("")
                setSelectedLineId(null)
                setShowTicketSearchModal(false)
                setShowOnHoldModal(false)
                refreshOnHoldCount()
            } catch (e: any) {
                setError(e?.userMessage || e.message)
            } finally {
                setLoading(false)
            }
        },
        [companyId, refreshOnHoldCount]
    )

    const handleOpenTicketSearch = useCallback(async () => {
        if (!companyId || !registerId) return
        setTicketSearchQuery("")
        try {
            const res = await salesService.listSales(
                companyId,
                registerId,
                "saved",
                ""
            )
            const sales = res.sales || []
            setAllTickets(sales)
            setTicketList(sales)
        } catch {}
        setShowTicketSearchModal(true)
    }, [companyId, registerId])

    const handleSearchTickets = useCallback(
        (query: string) => {
            setTicketSearchQuery(query)
            // Filtrage côté client : le backend ne supporte pas la recherche
            // par numéro de ticket (sale_number / id)
            const q = query.trim().toLowerCase()
            if (!q) {
                setTicketList(allTickets)
                return
            }
            setTicketList(
                allTickets.filter((ticket) => {
                    // On cherche uniquement dans ce qui est affiché à l'écran
                    const num = (
                        ticket.sale_number ||
                        (ticket.id || "").slice(0, 8)
                    ).toLowerCase()
                    const customer = (
                        ticket.customer_name || ""
                    ).toLowerCase()
                    return num.includes(q) || customer.includes(q)
                })
            )
        },
        [allTickets]
    )

    const handleOpenOnHold = useCallback(async () => {
        if (!companyId || !registerId) return
        try {
            const res = await salesService.listSales(
                companyId,
                registerId,
                "on_hold",
                ""
            )
            setTicketList(res.sales || [])
        } catch {}
        setShowOnHoldModal(true)
    }, [companyId, registerId])

    const createTicketMovementLine = useCallback(
        async ({
            direction,
            amountMinor,
            paymentMethodType,
            note,
        }: {
            direction: "in" | "out"
            amountMinor: number
            paymentMethodType: string
            note?: string
        }) => {
            const normalizedMethodType =
                normalizePaymentMethodType(paymentMethodType)
            if (
                !companyId ||
                !outletId ||
                !registerId ||
                !activeSession?.id ||
                amountMinor <= 0 ||
                (normalizedMethodType !== "cash" &&
                    normalizedMethodType !== "check")
            ) {
                return
            }

            await cashSessionService.recordCashMovement(companyId, {
                outletId,
                registerId,
                cashSessionId: activeSession.id,
                direction,
                amountMinor: Math.round(amountMinor),
                currency: "EUR",
                reasonCode: "ticket",
                note,
                movementType: "ticket",
                paymentMethodType: normalizedMethodType,
            })

            const movementStartDate = buildMovementsStartDate()

            const [sessionRows, registerRowsResponse] = await Promise.all([
                cashSessionService.listMovementsBySession(
                    companyId,
                    activeSession.id
                ),
                cashSessionService.listCashMovements(companyId, registerId, {
                    startDate: movementStartDate,
                    limit: MOVEMENTS_PAGE_SIZE,
                    offset: 0,
                }),
            ])
            const registerRows = registerRowsResponse?.movements || []
            setSessionMovements(sessionRows || [])
            setRegisterMovements(registerRows)
            setRegisterMovementsStartDate(movementStartDate)
            setRegisterMovementsOffset(registerRows.length)
            setRegisterMovementsHasMore(
                Boolean(registerRowsResponse?.pagination?.hasMore)
            )
        },
        [
            companyId,
            outletId,
            registerId,
            activeSession?.id,
            buildMovementsStartDate,
        ]
    )

    const handleReversePayment = useCallback(
        async (paymentId: string) => {
            if (!sale) return
            setLoading(true)
            try {
                const paymentToReverse = payments.find(
                    (p) => p.id === paymentId
                )
                const method = paymentMethods.find(
                    (m) => m.id === paymentToReverse?.payment_method_id
                )
                const methodType = normalizePaymentMethodType(
                    paymentToReverse?.payment_method_type ||
                        method?.type ||
                        method?.payment_type ||
                        ""
                )
                const reversedAmountMinor = Math.abs(
                    Number(paymentToReverse?.amount_minor || 0)
                )

                if (String(sale.status || "").toLowerCase() !== "open") {
                    const reopenRes = await salesService.updateSaleStatus(
                        companyId,
                        sale.id,
                        "open"
                    )
                    setSale((prev: any) =>
                        prev
                            ? {
                                  ...prev,
                                  ...(reopenRes.sale || {}),
                                  status: "open",
                              }
                            : prev
                    )
                }

                const res = await paymentsService.reversePayment(
                    companyId,
                    sale.id,
                    paymentId
                )
                setPayments(res.payments || [])

                if (
                    reversedAmountMinor > 0 &&
                    (methodType === "cash" || methodType === "check")
                ) {
                    try {
                        await createTicketMovementLine({
                            direction: "out",
                            amountMinor: reversedAmountMinor,
                            paymentMethodType: methodType,
                            note: `Annulation règlement ${
                                method?.label || methodType
                            }`,
                        })
                    } catch (movementError: any) {
                        setError(
                            movementError?.userMessage ||
                                movementError?.message ||
                                String(
                                    t(
                                        "posPage.errors.reversePaymentMovementFailed"
                                    )
                                )
                        )
                    }
                }

                setShowReversePaymentModal(false)
            } catch (e: any) {
                setError(e?.userMessage || e.message)
            } finally {
                setLoading(false)
            }
        },
        [companyId, sale, payments, paymentMethods, createTicketMovementLine, t]
    )

    const refreshSessionMovements = useCallback(async () => {
        if (!companyId || !activeSession?.id) {
            setSessionMovements([])
            return
        }
        const rows = await cashSessionService.listMovementsBySession(
            companyId,
            activeSession.id
        )
        setSessionMovements(rows || [])
    }, [companyId, activeSession?.id])

    const refreshRegisterMovements = useCallback(
        async (startDate?: string) => {
            if (!companyId || !registerId) {
                setRegisterMovements([])
                setRegisterMovementsOffset(0)
                setRegisterMovementsHasMore(false)
                return
            }
            const movementStartDate = startDate || buildMovementsStartDate()
            const response = await cashSessionService.listCashMovements(
                companyId,
                registerId,
                {
                    startDate: movementStartDate,
                    limit: MOVEMENTS_PAGE_SIZE,
                    offset: 0,
                }
            )
            const rows = response?.movements || []
            setRegisterMovements(rows)
            setRegisterMovementsStartDate(movementStartDate)
            setRegisterMovementsOffset(rows.length)
            setRegisterMovementsHasMore(Boolean(response?.pagination?.hasMore))
        },
        [companyId, registerId, buildMovementsStartDate]
    )

    const handleLoadMoreRegisterMovements = useCallback(async () => {
        if (
            !companyId ||
            !registerId ||
            !registerMovementsHasMore ||
            registerMovementsLoadingMore
        ) {
            return
        }

        const movementStartDate =
            registerMovementsStartDate || buildMovementsStartDate()

        setRegisterMovementsLoadingMore(true)
        try {
            const response = await cashSessionService.listCashMovements(
                companyId,
                registerId,
                {
                    startDate: movementStartDate,
                    limit: MOVEMENTS_PAGE_SIZE,
                    offset: registerMovementsOffset,
                }
            )
            const nextRows = response?.movements || []
            setRegisterMovements((prev) => [...prev, ...nextRows])
            setRegisterMovementsOffset((prev) => prev + nextRows.length)
            setRegisterMovementsHasMore(Boolean(response?.pagination?.hasMore))
            setRegisterMovementsStartDate(movementStartDate)
        } catch (e: any) {
            setError(
                e?.userMessage ||
                    e?.message ||
                    "Impossible de charger davantage de mouvements."
            )
        } finally {
            setRegisterMovementsLoadingMore(false)
        }
    }, [
        companyId,
        registerId,
        registerMovementsHasMore,
        registerMovementsLoadingMore,
        registerMovementsOffset,
        registerMovementsStartDate,
        buildMovementsStartDate,
    ])

    useEffect(() => {
        refreshSessionMovements().catch(() => {
            setSessionMovements([])
        })
    }, [refreshSessionMovements])

    const handleOpenMovementsModal = useCallback(async () => {
        setSessionMovementsLoading(true)
        try {
            const movementStartDate = buildMovementsStartDate()
            await Promise.all([
                refreshSessionMovements(),
                refreshRegisterMovements(movementStartDate),
            ])
            setShowMovementsModal(true)
        } catch (e: any) {
            setError(
                e?.userMessage ||
                    e?.message ||
                    String(t("posPage.errors.loadMovementsFailed"))
            )
        } finally {
            setSessionMovementsLoading(false)
        }
    }, [refreshSessionMovements, refreshRegisterMovements, t])

    const handleSubmitMovement = useCallback(async () => {
        if (!activeSession) {
            setError(String(t("posPage.errors.noActiveSession")))
            return
        }

        const comment = movementCommentInput.trim()
        if (!comment) {
            setError(String(t("posPage.errors.manualMovementCommentRequired")))
            return
        }

        if (movementAmountMinor <= 0) {
            setError(String(t("posPage.errors.amountMustBePositive")))
            return
        }

        if (
            movementDirection === "out" &&
            movementAmountMinor > selectedAvailableMinor
        ) {
            setError(
                String(
                    t("posPage.errors.outExceedsAvailableFund", {
                        method:
                            movementPaymentMethod === "cheque"
                                ? t("posPage.paymentMethods.check")
                                : t("posPage.paymentMethods.cash"),
                        amount: (selectedAvailableMinor / 100).toFixed(2),
                    })
                )
            )
            return
        }

        if (movementPaymentMethod === "cheque") {
            if (movementChequeCount <= 0) {
                setError(String(t("posPage.errors.checkCountRequired")))
                return
            }

            if (
                movementDirection === "out" &&
                movementChequeCount > availableChequeCount
            ) {
                setError(
                    String(
                        t("posPage.errors.outExceedsAvailableChecks", {
                            count: availableChequeCount,
                        })
                    )
                )
                return
            }
        }

        setLoading(true)
        setError(null)
        try {
            const movementNote =
                movementPaymentMethod === "cheque"
                    ? `${comment} · Nb cheques: ${movementChequeCount}`
                    : comment

            await cashSessionService.recordCashMovement(companyId, {
                outletId,
                registerId,
                cashSessionId: activeSession.id,
                direction: movementDirection,
                amountMinor: movementAmountMinor,
                currency: "EUR",
                reasonCode: movementDirection === "in" ? "entree" : "sortie",
                note: movementNote,
                movementType:
                    movementDirection === "in" ? "manual_in" : "manual_out",
                paymentMethodType: movementPaymentMethod,
            })

            setMovementAmountInput("")
            setMovementChequeCountInput("")
            setMovementCommentInput("")
            setMovementPaymentMethod("cash")
            setShowAddMovementModal(false)
            setSuccessMsg(String(t("posPage.messages.cashMovementSaved")))
            await Promise.all([
                refreshSessionMovements(),
                refreshRegisterMovements(),
            ])
        } catch (e: any) {
            setError(
                e?.userMessage ||
                    e?.message ||
                    String(t("posPage.errors.saveMovementFailed"))
            )
        } finally {
            setLoading(false)
        }
    }, [
        activeSession,
        movementCommentInput,
        movementAmountMinor,
        movementChequeCount,
        movementDirection,
        movementPaymentMethod,
        selectedAvailableMinor,
        availableChequeCount,
        companyId,
        outletId,
        registerId,
        refreshSessionMovements,
        refreshRegisterMovements,
        t,
    ])

    const handleOpenCloseOptions = useCallback(async () => {
        if (!activeSession) {
            setError(String(t("posPage.errors.noActiveSessionToClose")))
            return
        }
        setLoading(true)
        try {
            await refreshSessionMovements()
            setShowCloseOptionsModal(true)
        } catch (e: any) {
            setError(
                e?.message ||
                    String(t("posPage.errors.prepareCloseOptionsFailed"))
            )
        } finally {
            setLoading(false)
        }
    }, [activeSession, refreshSessionMovements, t])

    const handleCloseTheoretical = useCallback(async () => {
        if (!activeSession) return
        setLoading(true)
        setError(null)
        try {
            const res = await cashSessionService.closeSessionTheoretical(
                companyId,
                activeSession.id
            )
            setSuccessMsg(
                String(t("posPage.messages.sessionClosedTheoretical"))
            )
            setShowCloseOptionsModal(false)
            setActiveSession(null)
            setShowOpenSessionModal(false)
            setAllRegisters((prev) =>
                prev.map((r: any) =>
                    r.id === registerId ? { ...r, status: "free" } : r
                )
            )
            setPosPrechecks((prev: any) =>
                prev ? { ...prev, hasFreeRegister: true } : prev
            )
            clearPos()
            redirectToRegisterSelection({
                selectedCompanyId: companyId || undefined,
                selectedOutletId: outletId || undefined,
                selectedRegisterId: registerId || undefined,
            })
            if (res?.zReport) {
                // keep payload available for future UI extension
            }
        } catch (e: any) {
            setError(
                e?.message || String(t("posPage.errors.closeSessionFailed"))
            )
        } finally {
            setLoading(false)
        }
    }, [
        activeSession,
        companyId,
        outletId,
        registerId,
        redirectToRegisterSelection,
        clearPos,
        t,
    ])

    const handleCloseManual = useCallback(
        async (countedCashMinor: number) => {
            if (!activeSession) return

            if (!Number.isFinite(countedCashMinor) || countedCashMinor < 0) {
                setError(
                    String(t("posPage.errors.countedAmountPositiveOrZero"))
                )
                return
            }

            setLoading(true)
            setError(null)
            try {
                const res = await cashSessionService.closeSession(
                    companyId,
                    activeSession.id,
                    {
                        countedCashMinor: Math.round(countedCashMinor),
                    }
                )
                setSuccessMsg(String(t("posPage.messages.sessionClosedManual")))
                setShowManualCloseModal(false)
                setShowCloseOptionsModal(false)
                setActiveSession(null)
                setShowOpenSessionModal(false)
                setAllRegisters((prev) =>
                    prev.map((r: any) =>
                        r.id === registerId ? { ...r, status: "free" } : r
                    )
                )
                setPosPrechecks((prev: any) =>
                    prev ? { ...prev, hasFreeRegister: true } : prev
                )
                clearPos()
                redirectToRegisterSelection({
                    selectedCompanyId: companyId || undefined,
                    selectedOutletId: outletId || undefined,
                    selectedRegisterId: registerId || undefined,
                })
                if (res?.zReport) {
                    // keep payload available for future UI extension
                }
            } catch (e: any) {
                setError(
                    e?.message || String(t("posPage.errors.closeSessionFailed"))
                )
            } finally {
                setLoading(false)
            }
        },
        [
            activeSession,
            companyId,
            outletId,
            registerId,
            redirectToRegisterSelection,
            clearPos,
            t,
        ]
    )

    const handleTotal = () => {
        if (!sale || lines.length === 0) return
        setError(null)
        setLoading(true)

        salesService
            .totalizeSale(companyId, sale.id)
            .then((res) => {
                setSale(res.sale)
                setIsCartLocked(
                    Boolean(res.sale?.cart_locked_at) ||
                        Number(res.total_paid_minor || 0) > 0
                )
            })
            .catch((e: any) => {
                setError(e?.userMessage || e.message)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const handleRecordPayment = useCallback(async () => {
        if (!selectedPaymentMethodId || enteredAmountMinor <= 0) return
        if (!canCollectPayment) {
            setError(String(t("posPage.errors.totalizeBeforePayment")))
            return
        }

        const selectedMethod = selectedPaymentMethod
        if (!selectedMethod) {
            setError(String(t("posPage.errors.paymentMethodNotFound")))
            return
        }

        const methodType = normalizePaymentMethodType(
            selectedMethod.type || selectedMethod.payment_type || ""
        )

        const amountMinor = enteredAmountMinor

        if (amountMinor > remainingMinor && methodType !== "cash") {
            setError(
                String(
                    t("posPage.errors.amountExceedsRemaining", {
                        amount: (remainingMinor / 100).toFixed(2),
                    })
                )
            )
            return
        }
        setError(null)
        setLoading(true)
        try {
            const s = await ensureSale()
            const capturedAmountMinor =
                methodType === "cash"
                    ? Math.min(amountMinor, remainingMinor)
                    : amountMinor
            const res = await paymentsService.recordPayment(companyId, s.id, {
                paymentMethodId: selectedPaymentMethodId,
                amount: { currency: "EUR", amountMinor: capturedAmountMinor },
                ...(methodType === "cash"
                    ? { amountTenderedMinor: amountMinor }
                    : {}),
            })
            setPayments((prev) => [...prev, res.payment])
            setIsCartLocked(true)
            setSale((prev: any) =>
                prev
                    ? {
                          ...prev,
                          cart_locked_at:
                              prev.cart_locked_at || new Date().toISOString(),
                      }
                    : prev
            )

            if (methodType === "check") {
                try {
                    await createTicketMovementLine({
                        direction: "in",
                        amountMinor: capturedAmountMinor,
                        paymentMethodType: methodType,
                        note: `Encaissement ${
                            selectedMethod.label ||
                            String(t("posPage.paymentMethods.check"))
                        }`,
                    })
                } catch (movementError: any) {
                    setError(
                        movementError?.userMessage ||
                            movementError?.message ||
                            String(t("posPage.errors.checkMovementSaveFailed"))
                    )
                }
            }

            setDisplayValue("")
            setSelectedPaymentMethodId(null)
        } catch (e: any) {
            setError(e?.userMessage || e.message)
        } finally {
            setLoading(false)
        }
    }, [
        selectedPaymentMethodId,
        enteredAmountMinor,
        remainingMinor,
        companyId,
        ensureSale,
        selectedPaymentMethod,
        canCollectPayment,
        createTicketMovementLine,
        t,
    ])

    const handleFinalize = useCallback(async () => {
        if (!sale) return
        setError(null)
        setLoading(true)
        try {
            if (String(sale.status || "").toLowerCase() !== "open") {
                const reopenRes = await salesService.updateSaleStatus(
                    companyId,
                    sale.id,
                    "open"
                )
                setSale((prev: any) =>
                    prev
                        ? {
                              ...prev,
                              ...(reopenRes.sale || {}),
                              status: "open",
                          }
                        : prev
                )
            }

            const res = await salesService.finalizeSale(companyId, sale.id)
            setSale(res.sale)
            setCurrentReceipt(res.receipt || null)
            setShowFinalizeModal(true)
        } catch (e: any) {
            setError(e?.userMessage || e.message)
        } finally {
            setLoading(false)
        }
    }, [companyId, sale])

    /* ── receipt actions ── */
    const handlePrintTicket = useCallback(async () => {
        if (!companyId || !currentReceipt?.id) return
        try {
            const html = await documentsService.getHtmlPreview(
                companyId,
                currentReceipt.id
            )
            const win = window.open("", "_blank")
            if (win) {
                win.document.write(html)
                win.document.close()
                setTimeout(() => win.print(), 500)
            }
        } catch (e: any) {
            setError(e?.message || "Print failed")
        }
    }, [companyId, currentReceipt])

    const handleSendByEmail = useCallback(() => {
        if (!currentReceipt?.id) return
        const customerEmail = selectedCustomer?.email || ""
        setEmailInput(customerEmail)
        setEmailSuccess(false)
        setShowEmailInputModal(true)
    }, [currentReceipt, selectedCustomer])

    const handleConfirmEmailSend = useCallback(async () => {
        if (!companyId || !currentReceipt?.id || !emailInput) return
        setEmailSending(true)
        try {
            await documentsService.sendReceiptByEmail(
                companyId,
                currentReceipt.id,
                emailInput
            )
            setEmailSuccess(true)
            setShowEmailInputModal(false)
        } catch (e: any) {
            setError(e?.message || "Failed to send email")
        } finally {
            setEmailSending(false)
        }
    }, [companyId, currentReceipt, emailInput])

    const handleShowQrCode = useCallback(async () => {
        if (!currentReceipt?.id) return
        const publicUrl = documentsService.getPublicReceiptUrl(
            currentReceipt.id
        )
        try {
            const dataUrl = await QRCode.toDataURL(publicUrl, {
                width: 280,
                margin: 2,
            })
            setQrDataUrl(dataUrl)
            setShowQrModal(true)
        } catch (e: any) {
            setError(e?.message || "Failed to generate QR code")
        }
    }, [currentReceipt])

    /* ── numpad ── */
    const handleNumpad = useCallback(
        (val: string) => {
            if (val === "C") {
                setDisplayValue("")
                setDisplayDirty(true)
            } else {
                if (!displayDirty) {
                    setDisplayValue(val)
                    setDisplayDirty(true)
                } else {
                    setDisplayValue((prev) => prev + val)
                }
            }
        },
        [displayDirty]
    )

    useEffect(() => {
        if (!canOperate || showSearchModal) return

        const SCAN_MAX_GAP_MS = 45
        const SCAN_IDLE_FLUSH_MS = 120
        const SCAN_MIN_LENGTH = 3

        const resetScannerState = () => {
            scannerBufferRef.current = ""
            scannerLastKeyTsRef.current = 0
            scannerSequenceActiveRef.current = false
            scannerCaptureInProgressRef.current = false
            if (scannerFlushTimerRef.current != null) {
                window.clearTimeout(scannerFlushTimerRef.current)
                scannerFlushTimerRef.current = null
            }
        }

        const commitScan = () => {
            const scannedValue = scannerBufferRef.current.trim()
            resetScannerState()
            if (scannedValue.length < 1) return
            // Afficher la valeur dans l'input (visible si pas de match)
            setSearchValue(scannedValue)
            handleAddByRefRef.current?.(scannedValue)
        }

        const handleScannerKeyDown = (e: KeyboardEvent) => {
            if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return

            const target = e.target as HTMLElement | null
            const isSearchInputTarget = target === searchInputRef.current

            // Si un autre input/textarea est focalisé, ne pas intercepter
            const isOtherInputFocused =
                !isSearchInputTarget &&
                (target?.tagName === "INPUT" ||
                    target?.tagName === "TEXTAREA" ||
                    (target as HTMLElement | null)?.isContentEditable === true)
            if (isOtherInputFocused) {
                resetScannerState()
                return
            }

            const allowNativeSearchTyping =
                isSearchInputTarget &&
                (!isTabletScannerMode || scanInputManualMode)

            if (allowNativeSearchTyping) {
                resetScannerState()
                return
            }

            if (e.key === "Enter" || e.key === "Tab") {
                if (
                    scannerSequenceActiveRef.current &&
                    scannerBufferRef.current.length >= SCAN_MIN_LENGTH
                ) {
                    // Fin d'une séquence scanner rapide
                    e.preventDefault()
                    e.stopPropagation()
                    commitScan()
                } else if (
                    e.key === "Enter" &&
                    scannerBufferRef.current.trim().length >= 1
                ) {
                    // Saisie manuelle lente + Entrée : valider le buffer accumulé
                    e.preventDefault()
                    e.stopPropagation()
                    commitScan()
                } else {
                    resetScannerState()
                }
                return
            }

            if (e.key.length !== 1) return

            const nowTs = Date.now()
            const delta = scannerLastKeyTsRef.current
                ? nowTs - scannerLastKeyTsRef.current
                : 0

            if (delta > SCAN_MAX_GAP_MS) {
                scannerBufferRef.current = e.key
                scannerSequenceActiveRef.current = false
                scannerCaptureInProgressRef.current = false
            } else {
                // Frappe rapide (scanner) : séquence détectée
                scannerBufferRef.current += e.key
                scannerCaptureInProgressRef.current =
                    scannerBufferRef.current.length >= 2
                if (scannerBufferRef.current.length >= SCAN_MIN_LENGTH) {
                    scannerSequenceActiveRef.current = true
                }
            }

            scannerLastKeyTsRef.current = nowTs

            if (scannerFlushTimerRef.current != null) {
                window.clearTimeout(scannerFlushTimerRef.current)
            }

            scannerFlushTimerRef.current = window.setTimeout(() => {
                if (
                    scannerSequenceActiveRef.current &&
                    scannerBufferRef.current.length >= SCAN_MIN_LENGTH
                ) {
                    commitScan()
                } else {
                    resetScannerState()
                }
            }, SCAN_IDLE_FLUSH_MS)

            if (scannerCaptureInProgressRef.current) {
                e.preventDefault()
                e.stopPropagation()
            }
        }

        window.addEventListener("keydown", handleScannerKeyDown, true)

        return () => {
            window.removeEventListener("keydown", handleScannerKeyDown, true)
            resetScannerState()
        }
    }, [canOperate, showSearchModal, isTabletScannerMode, scanInputManualMode])

    useEffect(() => {
        if (!canCollectPayment) return
        const handleKeyDown = (e: KeyboardEvent) => {
            if (isTabletScannerMode && scannerCaptureInProgressRef.current)
                return
            if (
                e.target instanceof HTMLInputElement ||
                e.target instanceof HTMLTextAreaElement
            )
                return
            if (/^[0-9.]$/.test(e.key)) {
                e.preventDefault()
                handleNumpad(e.key)
            } else if (e.key === "Backspace") {
                e.preventDefault()
                setDisplayValue((prev) => prev.slice(0, -1))
                setDisplayDirty(true)
            }
        }
        window.addEventListener("keydown", handleKeyDown)
        return () => window.removeEventListener("keydown", handleKeyDown)
    }, [canCollectPayment, handleNumpad, isTabletScannerMode])

    const ITEMS_PER_PAGE = 20

    /* ── search ── */
    const doSearch = useCallback(
        async (q: string, page: number = 0) => {
            if (!companyId) return
            setSearchLoading(true)
            try {
                const params = new URLSearchParams()
                if (q.trim().length > 0) params.append("q", q.trim())
                params.append("limit", String(ITEMS_PER_PAGE))
                params.append("offset", String(page * ITEMS_PER_PAGE))
                if (outletId) params.append("outletId", outletId)
                const res = await apiClient.get(
                    `/api/companies/${companyId}/items?${params.toString()}`
                )
                setSearchResults(res.items || [])
                setSearchTotal(res.total || 0)
            } catch {
                setSearchResults([])
                setSearchTotal(0)
            } finally {
                setSearchLoading(false)
            }
        },
        [companyId, outletId]
    )

    useEffect(() => {
        if (!showSearchModal) return
        const t = setTimeout(() => doSearch(searchValue, searchPage), 0)
        return () => clearTimeout(t)
    }, [searchValue, searchPage, showSearchModal, doSearch])

    // Focus les inputs des modals après la fin de l'animation d'ouverture du Dialog
    useEffect(() => {
        if (!showSearchModal) return
        const t = setTimeout(() => searchModalInputRef.current?.focus(), 80)
        return () => clearTimeout(t)
    }, [showSearchModal])

    useEffect(() => {
        if (!showCommentModal) return
        const t = setTimeout(() => commentModalInputRef.current?.focus(), 80)
        return () => clearTimeout(t)
    }, [showCommentModal])

    useEffect(() => {
        if (!showTicketSearchModal) return
        const t = setTimeout(() => ticketSearchModalInputRef.current?.focus(), 80)
        return () => clearTimeout(t)
    }, [showTicketSearchModal])

    // Réinitialise le highlight sur le 1er résultat à chaque nouvelle recherche
    useEffect(() => {
        setHighlightedSearchIndex(0)
    }, [searchResults])

    // Recherche un article par référence/SKU/barcode et l'ajoute au panier.
    // En cas d'absence de match, l'input n'est pas vidé : l'utilisateur peut
    // continuer à taper jusqu'à ce qu'une référence corresponde.
    const handleAddByRef = useCallback(
        async (val: string) => {
            if (isCartLocked || !companyId) return
            const trimmed = val.trim()
            if (!trimmed) return
            try {
                const params = new URLSearchParams()
                params.append("q", trimmed)
                params.append("limit", "10")
                if (outletId) params.append("outletId", outletId)
                const res = await apiClient.get(
                    `/api/companies/${companyId}/items?${params.toString()}`
                )
                const lower = trimmed.toLowerCase()
                const match = (res.items || []).find(
                    (it: any) =>
                        it.sku?.toLowerCase() === lower ||
                        it.barcode?.toLowerCase() === lower ||
                        it.reference?.toLowerCase() === lower
                )
                if (match) {
                    handleAddItem(match)
                    setSearchValue("")
                    searchInputRef.current?.focus()
                }
                // Pas de match → on laisse la valeur dans l'input
            } catch {
                // ignore
            }
        },
        [companyId, outletId, isCartLocked, handleAddItem]
    )
    handleAddByRefRef.current = handleAddByRef

    // Fallback auto-add : déclenché si le scanner n'envoie pas de touche Enter
    useEffect(() => {
        if (showSearchModal || isCartLocked || !companyId) return
        const val = searchValue.trim()
        if (val.length < 1) return
        const timeOut = setTimeout(() => {
            handleAddByRef(val)
        }, 400)
        return () => clearTimeout(timeOut)
    }, [
        searchValue,
        showSearchModal,
        isCartLocked,
        companyId,
        handleAddByRef,
    ])

    // Redirection globale : toute frappe clavier sans focus sur un input
    // est redirigée vers le champ de recherche (visible à l'écran)
    useEffect(() => {
        if (!canOperate || showSearchModal) return

        const handleGlobalTyping = (e: KeyboardEvent) => {
            if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return

            const target = e.target as HTMLElement | null
            // Si un input ou textarea est déjà focalisé, ne pas intercepter
            if (
                target?.tagName === "INPUT" ||
                target?.tagName === "TEXTAREA" ||
                (target as HTMLElement | null)?.isContentEditable === true
            ) return

            const input = searchInputRef.current
            if (!input || input.disabled) return

            if (e.key.length === 1) {
                e.preventDefault()
                input.focus({ preventScroll: true })
                setSearchValue((prev) => prev + e.key)
            } else if (e.key === "Backspace") {
                e.preventDefault()
                input.focus({ preventScroll: true })
                setSearchValue((prev) => prev.slice(0, -1))
            }
        }

        window.addEventListener("keydown", handleGlobalTyping, true)
        return () => window.removeEventListener("keydown", handleGlobalTyping, true)
    }, [canOperate, showSearchModal])

    /* ── quick action button style helper ── */
    const actionBtn = (
        label: string,
        icon: React.ReactNode,
        onClick: () => void,
        opts?: { color?: string; disabled?: boolean }
    ) => {
        const isError = opts?.color === "error"
        const isWarning = opts?.color === "warning"
        const disabled = opts?.disabled || false
        return (
            <button
                key={label}
                onClick={onClick}
                disabled={disabled}
                style={{
                    padding: "10px 10px",
                    minHeight: 56,
                    backgroundColor: disabled
                        ? "#f9fafb"
                        : isError
                        ? "#fee2e2"
                        : isWarning
                        ? "#fef3c7"
                        : "#f9fafb",
                    color: disabled
                        ? "#9ca3af"
                        : isError
                        ? "#dc2626"
                        : isWarning
                        ? "#b45309"
                        : "#374151",
                    border: `1px solid ${
                        disabled
                            ? "#e5e7eb"
                            : isError
                            ? "#fecaca"
                            : isWarning
                            ? "#fde68a"
                            : "#e5e7eb"
                    }`,
                    borderRadius: "8px",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    cursor: disabled ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                }}
            >
                {icon}
                {label}
            </button>
        )
    }

    /* ═══════════ PAYMENT PANEL CONTENT (shared between desktop & overlay) ═══════════ */
    const renderPaymentPanel = (compact = false) => (
        <>
            {!compact && (
                <Typography
                    sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        mb: 1.5,
                        color: "#6b7280",
                    }}
                >
                    {t("posPage.payment.title")}
                </Typography>
            )}

            {/* TOTAL / ENCAISSER button (moved up for small screens) */}
            {!canCollectPayment ? (
                <button
                    onClick={handleTotal}
                    disabled={lines.length === 0 || !canOperate}
                    style={{
                        width: "100%",
                        padding: compact ? "10px" : "14px",
                        fontSize: "1rem",
                        fontWeight: 700,
                        backgroundColor:
                            lines.length === 0 || !canOperate
                                ? "#d1d5db"
                                : "#2563eb",
                        color:
                            lines.length === 0 || !canOperate
                                ? "#6b7280"
                                : "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor:
                            lines.length === 0 || !canOperate
                                ? "not-allowed"
                                : "pointer",
                        marginBottom: compact ? 8 : 10,
                    }}
                >
                    {t("posPage.payment.total")}
                </button>
            ) : remainingMinor === 0 ? (
                <button
                    onClick={handleFinalize}
                    disabled={loading}
                    style={{
                        width: "100%",
                        padding: compact ? "10px" : "14px",
                        fontSize: "1rem",
                        fontWeight: 700,
                        backgroundColor: "#10b981",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: loading ? "not-allowed" : "pointer",
                        marginBottom: compact ? 8 : 10,
                    }}
                >
                    {t("posPage.payment.finalize")}
                </button>
            ) : (
                <button
                    onClick={handleRecordPayment}
                    disabled={!canSubmitPayment}
                    style={{
                        width: "100%",
                        padding: compact ? "10px" : "14px",
                        fontSize: "1rem",
                        fontWeight: 700,
                        backgroundColor: !canSubmitPayment
                            ? "#d1d5db"
                            : "#10b981",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: !canSubmitPayment ? "not-allowed" : "pointer",
                        marginBottom: compact ? 8 : 10,
                    }}
                >
                    {t("posPage.payment.collect")}
                </button>
            )}

            {/* Payment method buttons */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 0.5,
                    mb: 0.5,
                }}
            >
                {paymentMethods.slice(0, 3).map((m) => {
                    const isActive = selectedPaymentMethodId === m.id
                    const methodType = m.type || m.payment_type
                    return (
                        <button
                            key={m.id}
                            onClick={() => {
                                setSelectedPaymentMethodId(m.id)
                                setDisplayValue(
                                    (remainingMinor / 100).toFixed(2)
                                )
                                setDisplayDirty(false)
                            }}
                            disabled={!canCollectPayment}
                            style={{
                                padding: "10px 4px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                                backgroundColor: isActive
                                    ? "#7f1d1d"
                                    : "#f9fafb",
                                color: isActive
                                    ? "white"
                                    : !canCollectPayment
                                    ? "#9ca3af"
                                    : "#374151",
                                border: isActive
                                    ? "2px solid #7f1d1d"
                                    : "1px solid #e5e7eb",
                                borderRadius: "8px",
                                cursor: !canCollectPayment
                                    ? "not-allowed"
                                    : "pointer",
                                fontSize: "0.56rem",
                                fontWeight: 600,
                                minHeight: 58,
                                textTransform: "uppercase",
                            }}
                        >
                            {getPaymentMethodIcon(methodType, {
                                fontSize: 18,
                            })}
                            {m.label}
                        </button>
                    )
                })}
                <Tooltip title={t("posPage.payment.otherMethods")} arrow>
                    <span style={{ display: "block", width: "100%" }}>
                        <button
                            disabled={!canCollectPayment}
                            onClick={() => setShowOtherPayments(true)}
                            style={{
                                padding: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: !canCollectPayment
                                    ? "#e5e7eb"
                                    : "#f9fafb",
                                color: !canCollectPayment
                                    ? "#9ca3af"
                                    : "#374151",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                cursor: !canCollectPayment
                                    ? "not-allowed"
                                    : "pointer",
                                width: "100%",
                                minHeight: 58,
                            }}
                        >
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                        </button>
                    </span>
                </Tooltip>
            </Box>

            {/* Remaining amount */}
            {canCollectPayment && (
                <Box
                    sx={{
                        backgroundColor: "#fef3c7",
                        border: "1px solid #fde68a",
                        borderRadius: "8px",
                        p: compact ? 1 : 1.5,
                        mb: compact ? 1 : 1.5,
                        textAlign: "center",
                    }}
                >
                    <Typography sx={{ fontSize: "0.7rem", color: "#78350f" }}>
                        {t("posPage.payment.remaining")}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: compact ? "1.1rem" : "1.3rem",
                            fontWeight: 700,
                            color: "#b45309",
                        }}
                    >
                        {(remainingMinor / 100).toFixed(2)} €
                    </Typography>
                    {totalPaidMinor > 0 && (
                        <Typography
                            sx={{
                                fontSize: "0.7rem",
                                color: "#92400e",
                                mt: 0.25,
                            }}
                        >
                            {t("posPage.payment.alreadyPaid")}:{" "}
                            {(totalPaidMinor / 100).toFixed(2)} €
                        </Typography>
                    )}
                </Box>
            )}

            {/* Display */}
            <Box
                sx={{
                    backgroundColor: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    p: compact ? 1 : 2,
                    mb: compact ? 1 : 1.5,
                    textAlign: "right",
                }}
            >
                <Typography
                    sx={{
                        fontSize: compact ? "1.5rem" : "1.75rem",
                        fontWeight: 700,
                        fontFamily: "monospace",
                        minHeight: compact ? 32 : 40,
                    }}
                >
                    {displayValue || "0"}
                </Typography>
            </Box>

            {canCollectPayment &&
                selectedPaymentMethodId &&
                enteredAmountMinor > 0 && (
                    <Box
                        sx={{
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            p: compact ? 1 : 1.25,
                            mb: compact ? 1 : 1.5,
                            backgroundColor: isCashPaymentSelected
                                ? "#ecfdf5"
                                : "#f9fafb",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                flexWrap: "wrap",
                            }}
                        >
                            <Typography
                                sx={{ fontSize: "0.8rem", color: "#374151" }}
                            >
                                {t("posPage.payment.enteredAmount")}:{" "}
                                {(enteredAmountMinor / 100).toFixed(2)} €
                            </Typography>
                            <Typography
                                sx={{ fontSize: "0.8rem", color: "#374151" }}
                            >
                                {t("posPage.payment.capturedAmount")}:{" "}
                                {(previewCapturedMinor / 100).toFixed(2)} €
                            </Typography>
                        </Box>
                        {isCashPaymentSelected ? (
                            <Typography
                                sx={{
                                    fontSize: "0.92rem",
                                    fontWeight: 700,
                                    color:
                                        previewChangeMinor > 0
                                            ? "#047857"
                                            : "#374151",
                                    mt: 0.5,
                                }}
                            >
                                {t("posPage.payment.change")}:{" "}
                                {(previewChangeMinor / 100).toFixed(2)} €
                            </Typography>
                        ) : overRemainingForNonCash ? (
                            <Typography
                                sx={{
                                    fontSize: "0.92rem",
                                    fontWeight: 700,
                                    color: "#b91c1c",
                                    mt: 0.5,
                                }}
                            >
                                {t("posPage.payment.nonCashExceedsRemaining")}
                            </Typography>
                        ) : null}
                    </Box>
                )}

            {/* Numpad */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 0.75,
                    mb: compact ? 1 : 1.5,
                }}
            >
                {[
                    "7",
                    "8",
                    "9",
                    "4",
                    "5",
                    "6",
                    "1",
                    "2",
                    "3",
                    "0",
                    ".",
                    "C",
                ].map((btn) => (
                    <button
                        key={btn}
                        onClick={() => handleNumpad(btn)}
                        disabled={!canCollectPayment}
                        style={{
                            padding: compact ? "14px" : "18px",
                            fontSize: compact ? "1.1rem" : "1.25rem",
                            fontWeight: 600,
                            backgroundColor: !canCollectPayment
                                ? "#e5e7eb"
                                : btn === "C"
                                ? "#7f1d1d"
                                : "#f9fafb",
                            color: !canCollectPayment
                                ? "#9ca3af"
                                : btn === "C"
                                ? "white"
                                : "#374151",
                            border: btn === "C" ? "none" : "1px solid #e5e7eb",
                            borderRadius: "8px",
                            cursor: !canCollectPayment
                                ? "not-allowed"
                                : "pointer",
                        }}
                    >
                        {btn}
                    </button>
                ))}
            </Box>
        </>
    )

    /* ═══════════ RENDER ═══════════ */
    if (bootstrapping) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    gap: 2,
                    backgroundColor: "#f9fafb",
                }}
            >
                <CircularProgress sx={{ color: "#7f1d1d" }} />
                <Typography variant="body2" color="text.secondary">
                    {t("common.loading")}
                </Typography>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                overflow: "hidden",
            }}
        >
            {/* ── HEADER ── */}
            <Box
                sx={{
                    backgroundColor: "white",
                    borderBottom: "1px solid #e5e7eb",
                    px: 3,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexShrink: 0,
                    gap: 1,
                    "@media (max-width: 1280px)": {
                        px: 1.5,
                        py: 1,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    <IconButton
                        aria-label="open navigation"
                        onClick={() => setNavDrawerOpen(true)}
                        sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: "#7f1d1d",
                            color: "white",
                            borderRadius: "10px",
                            "&:hover": { backgroundColor: "#991b1b" },
                            "@media (max-width: 1280px)": {
                                width: 34,
                                height: 34,
                                borderRadius: "8px",
                            },
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Drawer
                        variant="temporary"
                        open={navDrawerOpen}
                        onClose={() => setNavDrawerOpen(false)}
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            "& .MuiDrawer-paper": {
                                width: POS_DRAWER_WIDTH,
                                boxSizing: "border-box",
                                border: "none",
                                background:
                                    "linear-gradient(180deg, #7f1d1d 0%, #991b1b 100%)",
                                overflow: "hidden",
                            },
                        }}
                    >
                        <MainNav onNavigate={() => setNavDrawerOpen(false)} />
                    </Drawer>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            minWidth: 0,
                            maxWidth: { xs: 180, md: 300, lg: 380 },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: { xs: "1.25rem", lg: "1.45rem" },
                            marginRight: 1,
                        }}
                    >
                        {outletName}
                    </Typography>
                    <Chip
                        icon={
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: "#10b981",
                                    ml: 1,
                                }}
                            />
                        }
                        label={`${t("posPage.header.cashier")}: ${
                            user?.display_name || t("common.dash")
                        }`}
                        sx={{
                            backgroundColor: "#f3f4f6",
                            fontWeight: 500,
                            fontSize: "0.8rem",
                            maxWidth: { xs: 140, md: 170, lg: 220 },
                            height: { xs: 24, lg: 32 },
                            "& .MuiChip-label": {
                                px: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            },
                        }}
                    />
                    <Chip
                        icon={<PersonIcon sx={{ fontSize: 16 }} />}
                        label={`${t(
                            "posPage.header.register"
                        )}: ${registerName}`}
                        variant="outlined"
                        onClick={(e) => setCaisseAnchorEl(e.currentTarget)}
                        deleteIcon={
                            <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                        }
                        onDelete={(e: any) =>
                            setCaisseAnchorEl(
                                e.currentTarget.closest(".MuiChip-root")
                            )
                        }
                        sx={{
                            fontWeight: 500,
                            fontSize: "0.8rem",
                            cursor: "pointer",
                            maxWidth: { xs: 120, md: 180, lg: 220 },
                            height: { xs: 24, lg: 32 },
                            "& .MuiChip-label": {
                                px: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            },
                        }}
                    />
                    <Chip
                        icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
                        label={selectedCustomerDisplayName}
                        variant="outlined"
                        onClick={handleOpenCustomerModal}
                        onDelete={
                            selectedCustomer
                                ? handleClearSelectedCustomer
                                : undefined
                        }
                        sx={{
                            fontWeight: 500,
                            fontSize: "0.8rem",
                            cursor: "pointer",
                            maxWidth: { xs: 130, md: 160, lg: 220 },
                            height: { xs: 24, lg: 32 },
                            color: selectedCustomer ? "#1f2937" : "#9ca3af",
                            borderColor: selectedCustomer
                                ? "#d1d5db"
                                : "#e5e7eb",
                            "& .MuiChip-label": {
                                px: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            },
                        }}
                    />
                    <Popover
                        open={Boolean(caisseAnchorEl)}
                        anchorEl={caisseAnchorEl}
                        onClose={() => setCaisseAnchorEl(null)}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                        PaperProps={{
                            sx: { borderRadius: "10px", py: 1, minWidth: 220 },
                        }}
                    >
                        {[
                            {
                                label: t("posPage.header.movements"),
                                icon: (
                                    <MonetizationOnIcon sx={{ fontSize: 18 }} />
                                ),
                                onClick: () => {
                                    setCaisseAnchorEl(null)
                                    handleOpenMovementsModal()
                                },
                            },
                            {
                                label: t("posPage.header.close"),
                                icon: <LockIcon sx={{ fontSize: 18 }} />,
                                onClick: () => {
                                    setCaisseAnchorEl(null)
                                    handleOpenCloseOptions()
                                },
                                danger: true,
                            },
                        ].map((item) => (
                            <Box
                                key={item.label}
                                onClick={item.onClick}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    px: 2,
                                    py: 1.2,
                                    cursor: "pointer",
                                    color: (item as any).danger
                                        ? "#dc2626"
                                        : "#1f2937",
                                    "&:hover": { backgroundColor: "#f3f4f6" },
                                    fontSize: "0.85rem",
                                    fontWeight: 500,
                                }}
                            >
                                {item.icon}
                                {item.label}
                            </Box>
                        ))}
                    </Popover>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        fontSize: "0.8rem",
                        color: "#6b7280",
                        flexShrink: 0,
                        "@media (max-width: 1280px)": {
                            gap: 1,
                            fontSize: "0.72rem",
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                            whiteSpace: "nowrap",
                        }}
                    >
                        <CalendarTodayIcon
                            sx={{ fontSize: { xs: 14, lg: 16 } }}
                        />
                        <span style={{ fontWeight: 500 }}>{currentDate}</span>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                            whiteSpace: "nowrap",
                        }}
                    >
                        <AccessTimeIcon sx={{ fontSize: { xs: 14, lg: 16 } }} />
                        <span style={{ fontWeight: 500 }}>{currentTime}</span>
                    </Box>
                </Box>
            </Box>

            <Snackbar
                open={!!error}
                autoHideDuration={4500}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 2000 }}
            >
                <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{ width: "100%", fontWeight: 600 }}
                >
                    {error}
                </Alert>
            </Snackbar>

            {posPrechecks &&
                !posPrechecks.hasFreeRegister &&
                !activeSession && (
                    <Alert
                        severity="warning"
                        sx={{ mx: 2, mt: 1, flexShrink: 0 }}
                    >
                        {t("posPage.warnings.allRegistersBusy")}
                    </Alert>
                )}

            {/* ── MAIN 3-COLUMN AREA ── */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    gap: 2,
                    p: 2,
                    overflow: "hidden",
                }}
            >
                {/* ═══ LEFT: CART ═══ */}
                <Box
                    sx={{
                        flex: "1 1 0",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        overflow: "hidden",
                    }}
                >
                    {/* cart header */}
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            borderBottom: "1px solid #e5e7eb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <ShoppingCartIcon sx={{ fontSize: 20 }} />
                            <Typography
                                sx={{ fontSize: "1rem", fontWeight: 600 }}
                            >
                                {t("posPage.cart.title")}
                            </Typography>
                            {isCartLocked && (
                                <Chip
                                    label={t("posPage.cart.locked")}
                                    size="small"
                                    sx={{
                                        backgroundColor: "#d1fae5",
                                        color: "#065f46",
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                    }}
                                />
                            )}
                        </Box>
                        <Chip
                            label={t("posPage.cart.itemsCount", {
                                count: lines.length,
                            })}
                            size="small"
                            sx={{
                                backgroundColor: "#f3f4f6",
                                fontSize: "0.75rem",
                            }}
                        />
                    </Box>

                    {/* cart table */}
                    <Box sx={{ flex: 1, overflow: "auto" }}>
                        <table
                            style={{
                                width: "100%",
                                borderCollapse: "collapse",
                            }}
                        >
                            <thead>
                                <tr style={{ backgroundColor: "#f9fafb" }}>
                                    <th
                                        style={{
                                            padding: "10px 16px",
                                            textAlign: "left",
                                            fontSize: "0.7rem",
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            color: "#6b7280",
                                        }}
                                    >
                                        {t("posPage.cart.columns.item")}
                                    </th>
                                    <th
                                        style={{
                                            padding: "10px 16px",
                                            textAlign: "center",
                                            fontSize: "0.7rem",
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            color: "#6b7280",
                                        }}
                                    >
                                        {t("posPage.cart.columns.quantity")}
                                    </th>
                                    <th
                                        style={{
                                            padding: "10px 16px",
                                            textAlign: "right",
                                            fontSize: "0.7rem",
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            color: "#6b7280",
                                        }}
                                    >
                                        {t("posPage.cart.columns.unitPrice")}
                                    </th>
                                    <th
                                        style={{
                                            padding: "10px 16px",
                                            textAlign: "right",
                                            fontSize: "0.7rem",
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            color: "#6b7280",
                                        }}
                                    >
                                        {t("posPage.cart.columns.discount")}
                                    </th>
                                    <th
                                        style={{
                                            padding: "10px 16px",
                                            textAlign: "right",
                                            fontSize: "0.7rem",
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            color: "#6b7280",
                                        }}
                                    >
                                        {t("posPage.cart.columns.totalTtc")}
                                    </th>
                                    <th
                                        style={{
                                            padding: "10px 8px",
                                            textAlign: "center",
                                            fontSize: "0.7rem",
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            color: "#6b7280",
                                        }}
                                    >
                                        {t("posPage.cart.columns.actions")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((line) => (
                                    <tr
                                        key={line.id}
                                        onClick={() =>
                                            setSelectedLineId((prev) =>
                                                prev === line.id
                                                    ? null
                                                    : line.id
                                            )
                                        }
                                        style={{
                                            borderBottom: "1px solid #f3f4f6",
                                            opacity: isCartLocked ? 0.75 : 1,
                                            backgroundColor:
                                                selectedLineId === line.id
                                                    ? "#eff6ff"
                                                    : "transparent",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <td style={{ padding: "12px 16px" }}>
                                            <div
                                                style={{
                                                    fontWeight: 500,
                                                    fontSize: "0.85rem",
                                                }}
                                            >
                                                {line.label}
                                            </div>
                                            {line.sku && (
                                                <div
                                                    style={{
                                                        fontSize: "0.7rem",
                                                        color: "#6b7280",
                                                    }}
                                                >
                                                    {line.sku}
                                                </div>
                                            )}
                                        </td>
                                        <td
                                            style={{
                                                padding: "12px 8px",
                                                textAlign: "center",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    disabled={
                                                        isCartLocked || loading
                                                    }
                                                    onClick={() =>
                                                        handleUpdateQuantity(
                                                            line.id,
                                                            Number(
                                                                line.quantity
                                                            ) - 1
                                                        )
                                                    }
                                                    sx={{
                                                        minWidth: 26,
                                                        minHeight: 26,
                                                        fontSize: "1rem",
                                                        border: "1px solid #d1d5db",
                                                        borderRadius: "6px",
                                                        color: "#374151",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "#f3f4f6",
                                                        },
                                                    }}
                                                >
                                                    −
                                                </IconButton>
                                                <span
                                                    style={{
                                                        fontWeight: 600,
                                                        fontSize: "0.85rem",
                                                        minWidth: 24,
                                                        textAlign: "center",
                                                        display: "inline-block",
                                                    }}
                                                >
                                                    {line.quantity}
                                                </span>
                                                <IconButton
                                                    size="small"
                                                    disabled={
                                                        isCartLocked || loading
                                                    }
                                                    onClick={() =>
                                                        handleUpdateQuantity(
                                                            line.id,
                                                            Number(
                                                                line.quantity
                                                            ) + 1
                                                        )
                                                    }
                                                    sx={{
                                                        minWidth: 26,
                                                        minHeight: 26,
                                                        fontSize: "1rem",
                                                        border: "1px solid #d1d5db",
                                                        borderRadius: "6px",
                                                        color: "#374151",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "#f3f4f6",
                                                        },
                                                    }}
                                                >
                                                    +
                                                </IconButton>
                                            </Box>
                                        </td>
                                        <td
                                            style={{
                                                padding: "12px 16px",
                                                textAlign: "right",
                                                fontSize: "0.85rem",
                                                fontWeight: 500,
                                            }}
                                        >
                                            {formatMinor(
                                                getLineUnitPriceTtcMinor(line)
                                            )}
                                        </td>
                                        <td
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (!isCartLocked) {
                                                    setSelectedLineId(line.id)
                                                    setShowDiscountModal("line")
                                                }
                                            }}
                                            style={{
                                                padding: "12px 16px",
                                                textAlign: "right",
                                                fontSize: "0.85rem",
                                                cursor: isCartLocked
                                                    ? "default"
                                                    : "pointer",
                                                color:
                                                    line.discount_amount_minor >
                                                    0
                                                        ? "#dc2626"
                                                        : "#6b7280",
                                            }}
                                        >
                                            {line.discount_amount_minor > 0
                                                ? `−${formatMinor(
                                                      line.discount_amount_minor
                                                  )}${
                                                      line.discount_type ===
                                                      "percentage"
                                                          ? ` (${line.discount_value}%)`
                                                          : ""
                                                  }`
                                                : "—"}
                                        </td>
                                        <td
                                            style={{
                                                padding: "12px 16px",
                                                textAlign: "right",
                                                fontSize: "0.95rem",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {formatMinor(line.gross_minor)}
                                        </td>
                                        <td
                                            style={{
                                                padding: "12px 8px",
                                                textAlign: "center",
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    isCartLocked || loading
                                                }
                                                onClick={() =>
                                                    handleRemoveLine(line.id)
                                                }
                                                sx={{
                                                    backgroundColor:
                                                        isCartLocked
                                                            ? "#f3f4f6"
                                                            : "#fee2e2",
                                                    color: isCartLocked
                                                        ? "#9ca3af"
                                                        : "#dc2626",
                                                    width: 32,
                                                    height: 32,
                                                    "&:hover": {
                                                        backgroundColor:
                                                            "#fecaca",
                                                    },
                                                }}
                                            >
                                                <DeleteIcon
                                                    sx={{ fontSize: 16 }}
                                                />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                                {sale?.comment && (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            style={{ padding: "0 16px 12px" }}
                                        >
                                            <Box
                                                sx={{
                                                    backgroundColor: "#fef9e7",
                                                    border: "1px solid #fde68a",
                                                    borderRadius: "8px",
                                                    px: 2,
                                                    py: 1.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        mb: 0.25,
                                                    }}
                                                >
                                                    <ChatBubbleOutlineIcon
                                                        sx={{
                                                            fontSize: 14,
                                                            color: "#92400e",
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontSize: "0.7rem",
                                                            fontWeight: 700,
                                                            textTransform:
                                                                "uppercase",
                                                            color: "#92400e",
                                                        }}
                                                    >
                                                        {t(
                                                            "posPage.cart.comment"
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: "0.8rem",
                                                        color: "#78350f",
                                                    }}
                                                >
                                                    {sale.comment}
                                                </Typography>
                                            </Box>
                                        </td>
                                    </tr>
                                )}
                                {lines.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            style={{
                                                padding: "32px 16px",
                                                textAlign: "center",
                                                color: "#9ca3af",
                                                fontSize: "0.85rem",
                                            }}
                                        >
                                            {t("posPage.cart.empty")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </Box>

                    {/* cart footer - totals */}
                    <Box
                        sx={{
                            borderTop: "1px solid #e5e7eb",
                            backgroundColor: "#f9fafb",
                            px: 2,
                            py: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                mb: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: "0.8rem",
                                }}
                            >
                                <span style={{ color: "#6b7280" }}>
                                    {t("posPage.cart.subtotal")}
                                </span>
                                <span style={{ fontWeight: 500 }}>
                                    {formatMinor(subTotalMinor)}
                                </span>
                            </Box>
                            {globalDiscountAmountMinor > 0 && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        fontSize: "0.8rem",
                                    }}
                                >
                                    <span
                                        style={{
                                            color: "#dc2626",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Remise globale
                                        {sale?.global_discount_type ===
                                        "percentage"
                                            ? ` (${sale.global_discount_value}%)`
                                            : ""}
                                    </span>
                                    <span
                                        style={{
                                            color: "#dc2626",
                                            fontWeight: 600,
                                        }}
                                    >
                                        −
                                        {formatMinor(globalDiscountAmountMinor)}
                                    </span>
                                </Box>
                            )}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: "0.8rem",
                                }}
                            >
                                <span style={{ color: "#6b7280" }}>
                                    {t("posPage.cart.totalExclTax")}
                                </span>
                                <span style={{ fontWeight: 500 }}>
                                    {formatMinor(netTotalMinor)}
                                </span>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: "0.8rem",
                                }}
                            >
                                <span style={{ color: "#6b7280" }}>
                                    {t("posPage.cart.vat")}
                                </span>
                                <span style={{ fontWeight: 500 }}>
                                    {formatMinor(vatTotalMinor)}
                                </span>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                borderTop: "1px solid #d1d5db",
                                pt: 1.5,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                sx={{ fontSize: "1rem", fontWeight: 700 }}
                            >
                                {t("posPage.cart.totalTtc")}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "1.75rem",
                                    fontWeight: 700,
                                    color: "#7f1d1d",
                                }}
                            >
                                {formatMinor(grossTotalMinor)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* ═══ CENTER: SEARCH + ACTIONS ═══ */}
                <Box
                    sx={{
                        width: 280,
                        flexShrink: 0,
                        display: "flex",
                        "@media (max-width: 1160px)": {
                            display: showPaymentOverlay ? "none" : "flex",
                        },
                        flexDirection: "column",
                        gap: 1.5,
                        overflow: "auto",
                        position: "relative",
                    }}
                >
                    {/* Search */}
                    <Box
                        sx={{
                            backgroundColor: "white",
                            p: 2,
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                        }}
                    >
                        <Box sx={{ position: "relative", mb: 1 }}>
                            <SearchIcon
                                sx={{
                                    position: "absolute",
                                    left: 10,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#9ca3af",
                                    fontSize: 20,
                                }}
                            />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder={String(
                                    t("posPage.search.inputPlaceholder")
                                )}
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                readOnly={
                                    isTabletScannerMode && !scanInputManualMode
                                }
                                inputMode={
                                    isTabletScannerMode && !scanInputManualMode
                                        ? "none"
                                        : "text"
                                }
                                onPointerDown={(e) => {
                                    if (
                                        !isTabletScannerMode ||
                                        scanInputManualMode
                                    )
                                        return
                                    e.preventDefault()
                                    setScanInputManualMode(true)
                                    window.setTimeout(() => {
                                        searchInputRef.current?.focus()
                                    }, 0)
                                }}
                                onKeyDown={(e) => {
                                    if (
                                        e.key === "Enter" &&
                                        searchValue.trim()
                                    ) {
                                        e.preventDefault()
                                        handleAddByRef(searchValue)
                                    }
                                }}
                                onBlur={() => {
                                    if (isTabletScannerMode) {
                                        setScanInputManualMode(false)
                                    }
                                }}
                                disabled={isCartLocked || !canOperate}
                                style={{
                                    width: "100%",
                                    paddingLeft: 36,
                                    paddingRight: 12,
                                    paddingTop: 10,
                                    paddingBottom: 10,
                                    border: "1px solid #d1d5db",
                                    borderRadius: "8px",
                                    fontSize: "0.8rem",
                                    boxSizing: "border-box",
                                    outline: "none",
                                }}
                            />
                        </Box>
                        <button
                            onClick={() => setShowSearchModal(true)}
                            disabled={isCartLocked || !canOperate}
                            style={{
                                width: "100%",
                                padding: "10px",
                                backgroundColor:
                                    isCartLocked || !canOperate
                                        ? "#d1d5db"
                                        : "#7f1d1d",
                                color:
                                    isCartLocked || !canOperate
                                        ? "#6b7280"
                                        : "white",
                                border: "none",
                                borderRadius: "8px",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                cursor:
                                    isCartLocked || !canOperate
                                        ? "not-allowed"
                                        : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "6px",
                            }}
                        >
                            <SearchIcon sx={{ fontSize: 18 }} />
                            {t("posPage.search.button")}
                        </button>
                    </Box>

                    {/* Quick Actions */}
                    <Box
                        sx={{
                            backgroundColor: "white",
                            p: 2,
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                mb: 1,
                                color: "#6b7280",
                            }}
                        >
                            {t("posPage.quickActions.title")}
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 1,
                            }}
                        >
                            {actionBtn(
                                String(t("posPage.quickActions.lineDiscount")),
                                <LocalOfferIcon sx={{ fontSize: 16 }} />,
                                () => {
                                    if (!selectedLineId) return
                                    setShowDiscountModal("line")
                                },
                                { disabled: isCartLocked || !selectedLineId }
                            )}
                            {actionBtn(
                                String(
                                    t("posPage.quickActions.globalDiscount")
                                ),
                                <LocalOfferIcon sx={{ fontSize: 16 }} />,
                                () => {
                                    if (lines.length === 0) return
                                    setShowDiscountModal("global")
                                },
                                { disabled: isCartLocked || lines.length === 0 }
                            )}
                            {actionBtn(
                                String(t("posPage.quickActions.comment")),
                                <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />,
                                () => {
                                    setCommentDraft(sale?.comment || "")
                                    setShowCommentModal(true)
                                },
                                { disabled: isCartLocked || !sale }
                            )}
                            {actionBtn(
                                String(t("posPage.quickActions.deleteLine")),
                                <DeleteIcon sx={{ fontSize: 16 }} />,
                                () => {
                                    if (selectedLineId)
                                        handleRemoveLine(selectedLineId)
                                },
                                {
                                    color: "error",
                                    disabled: isCartLocked || !selectedLineId,
                                }
                            )}
                        </Box>
                    </Box>

                    {/* Ticket Management */}
                    <Box
                        sx={{
                            backgroundColor: "white",
                            p: 2,
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            flex: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                mb: 1,
                                color: "#6b7280",
                            }}
                        >
                            {t("posPage.ticket.title")}
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 1,
                            }}
                        >
                            {actionBtn(
                                String(t("posPage.ticket.actions.new")),
                                <NoteAddIcon sx={{ fontSize: 16 }} />,
                                handleNewSale
                            )}
                            {actionBtn(
                                String(t("posPage.ticket.actions.save")),
                                <SaveIcon sx={{ fontSize: 16 }} />,
                                handleSaveTicket,
                                { disabled: !sale || lines.length === 0 }
                            )}
                            {actionBtn(
                                `${t("posPage.ticket.actions.onHold")}${
                                    onHoldCount > 0 ? ` (${onHoldCount})` : ""
                                }`,
                                <ArchiveIcon sx={{ fontSize: 16 }} />,
                                sale && lines.length > 0
                                    ? handleHoldTicket
                                    : handleOpenOnHold,
                                { color: "warning" }
                            )}
                            {actionBtn(
                                String(t("posPage.ticket.actions.search")),
                                <SearchIcon sx={{ fontSize: 16 }} />,
                                handleOpenTicketSearch
                            )}
                            {actionBtn(
                                String(
                                    t("posPage.ticket.actions.cancelPayment")
                                ),
                                <UndoIcon sx={{ fontSize: 16 }} />,
                                () => setShowReversePaymentModal(true),
                                {
                                    color: "error",
                                    disabled: totalPaidMinor <= 0,
                                }
                            )}
                            {actionBtn(
                                String(t("posPage.ticket.actions.payments")),
                                <ReceiptIcon sx={{ fontSize: 16 }} />,
                                () => setShowPaymentHistoryModal(true),
                                { disabled: payments.length === 0 }
                            )}
                        </Box>
                    </Box>

                    {/* Toggle payment overlay arrow (visible ≤1160px) */}
                    <Box
                        onClick={() => setShowPaymentOverlay((p) => !p)}
                        sx={{
                            display: { xs: "flex", xl: "none" },
                            "@media (min-width: 1161px)": { display: "none" },
                            "@media (max-width: 1160px)": { display: "flex" },
                            alignItems: "center",
                            justifyContent: "center",
                            py: 1,
                            mt: "auto",
                            cursor: "pointer",
                            backgroundColor: "#7f1d1d",
                            borderRadius: "8px",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            gap: 1,
                            "&:hover": { backgroundColor: "#991b1b" },
                        }}
                    >
                        {showPaymentOverlay ? (
                            <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                        ) : (
                            <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
                        )}
                        {showPaymentOverlay
                            ? t("posPage.payment.hide")
                            : t("posPage.payment.title")}
                    </Box>
                </Box>

                {/* ═══ RIGHT: PAYMENT PANEL ═══ */}
                <Box
                    sx={{
                        width: 320,
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        p: 1.25,
                        overflow: "hidden",
                        "@media (max-width: 1160px)": {
                            display: showPaymentOverlay ? "flex" : "none",
                        },
                    }}
                >
                    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                        {renderPaymentPanel(true)}
                    </Box>
                    <Box
                        onClick={() => setShowPaymentOverlay(false)}
                        sx={{
                            display: { xs: "flex", xl: "none" },
                            "@media (min-width: 1161px)": { display: "none" },
                            alignItems: "center",
                            justifyContent: "center",
                            py: 1,
                            mt: 1,
                            cursor: "pointer",
                            backgroundColor: "#7f1d1d",
                            borderRadius: "8px",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            gap: 1,
                            flexShrink: 0,
                            "&:hover": { backgroundColor: "#991b1b" },
                        }}
                    >
                        <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                        {t("posPage.payment.hide")}
                    </Box>
                </Box>
            </Box>

            {/* ═══ SEARCH ARTICLE MODAL ═══ */}
            <Dialog
                open={showSearchModal}
                onClose={() => {
                    setShowSearchModal(false)
                    setSearchValue("")
                    setSearchResults([])
                    setSearchPage(0)
                    setSearchTotal(0)
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
                        {t("posPage.searchModal.title")}
                    </Typography>
                    <IconButton
                        onClick={() => {
                            setShowSearchModal(false)
                            setSearchValue("")
                            setSearchResults([])
                            setSearchPage(0)
                            setSearchTotal(0)
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        placeholder={String(
                            t("posPage.searchModal.placeholder")
                        )}
                        value={searchValue}
                        onChange={(e) => {
                            setSearchValue(e.target.value)
                            setSearchPage(0)
                        }}
                        inputRef={searchModalInputRef}
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <SearchIcon sx={{ color: "#9ca3af", mr: 1 }} />
                            ),
                        }}
                        onKeyDown={(e) => {
                            if (searchResults.length === 0) return
                            if (e.key === "ArrowDown") {
                                e.preventDefault()
                                setHighlightedSearchIndex((prev) =>
                                    Math.min(prev + 1, searchResults.length - 1)
                                )
                            } else if (e.key === "ArrowUp") {
                                e.preventDefault()
                                setHighlightedSearchIndex((prev) =>
                                    Math.max(prev - 1, 0)
                                )
                            } else if (e.key === "Enter") {
                                e.preventDefault()
                                const item = searchResults[highlightedSearchIndex]
                                if (!item) return
                                handleAddItem(item)
                                setShowSearchModal(false)
                                setSearchValue("")
                                setSearchResults([])
                                setSearchPage(0)
                                setSearchTotal(0)
                            }
                        }}
                    />
                    <Box
                        sx={{
                            maxHeight: 450,
                            overflow: "auto",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                        }}
                    >
                        {searchLoading ? (
                            <Box
                                sx={{
                                    textAlign: "center",
                                    py: 4,
                                    color: "#6b7280",
                                }}
                            >
                                {t("common.loading")}
                            </Box>
                        ) : searchResults.length > 0 ? (
                            <table
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                }}
                            >
                                <thead>
                                    <tr style={{ backgroundColor: "#f9fafb" }}>
                                        {[
                                            String(
                                                t(
                                                    "posPage.searchModal.columns.item"
                                                )
                                            ),
                                            String(
                                                t(
                                                    "posPage.searchModal.columns.reference"
                                                )
                                            ),
                                            String(
                                                t(
                                                    "posPage.searchModal.columns.category"
                                                )
                                            ),
                                            String(
                                                t(
                                                    "posPage.searchModal.columns.price"
                                                )
                                            ),
                                            String(
                                                t(
                                                    "posPage.searchModal.columns.stock"
                                                )
                                            ),
                                        ].map((col, i) => (
                                            <th
                                                key={col}
                                                style={{
                                                    padding: "10px 16px",
                                                    textAlign:
                                                        i >= 3
                                                            ? "right"
                                                            : "left",
                                                    fontSize: "0.7rem",
                                                    fontWeight: 600,
                                                    textTransform: "uppercase",
                                                    color: "#6b7280",
                                                }}
                                            >
                                                {col}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {searchResults.map((item, index) => (
                                        <tr
                                            key={item.id}
                                            onClick={() => {
                                                handleAddItem(item)
                                                setShowSearchModal(false)
                                                setSearchValue("")
                                                setSearchResults([])
                                                setSearchPage(0)
                                                setSearchTotal(0)
                                            }}
                                            onMouseEnter={() =>
                                                setHighlightedSearchIndex(index)
                                            }
                                            style={{
                                                cursor: "pointer",
                                                borderBottom:
                                                    "1px solid #f3f4f6",
                                                backgroundColor:
                                                    index ===
                                                    highlightedSearchIndex
                                                        ? "#ede9fe"
                                                        : "white",
                                            }}
                                        >
                                            <td
                                                style={{ padding: "12px 16px" }}
                                            >
                                                <div
                                                    style={{
                                                        fontWeight: 500,
                                                        fontSize: "0.85rem",
                                                    }}
                                                >
                                                    {item.name}
                                                </div>
                                            </td>
                                            <td
                                                style={{ padding: "12px 16px" }}
                                            >
                                                <div
                                                    style={{
                                                        fontSize: "0.8rem",
                                                        color: "#6b7280",
                                                        fontFamily: "monospace",
                                                    }}
                                                >
                                                    {item.sku}
                                                </div>
                                            </td>
                                            <td
                                                style={{ padding: "12px 16px" }}
                                            >
                                                <div
                                                    style={{
                                                        fontSize: "0.8rem",
                                                        color: "#374151",
                                                    }}
                                                >
                                                    {item.category_name || "—"}
                                                </div>
                                            </td>
                                            <td
                                                style={{
                                                    padding: "12px 16px",
                                                    textAlign: "right",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontWeight: 600,
                                                        fontSize: "0.85rem",
                                                    }}
                                                >
                                                    {item.unit_price_minor
                                                        ? formatMinor(
                                                              item.unit_price_minor
                                                          )
                                                        : "—"}
                                                </div>
                                            </td>
                                            <td
                                                style={{
                                                    padding: "12px 16px",
                                                    textAlign: "right",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontSize: "0.8rem",
                                                        color: "#6b7280",
                                                    }}
                                                >
                                                    —
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <Box
                                sx={{
                                    textAlign: "center",
                                    py: 4,
                                    color: "#6b7280",
                                }}
                            >
                                {t("posPage.searchModal.empty")}
                            </Box>
                        )}
                    </Box>
                    {/* Pagination */}
                    {searchTotal > ITEMS_PER_PAGE && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mt: 2,
                            }}
                        >
                            <Typography
                                sx={{ fontSize: "0.8rem", color: "#6b7280" }}
                            >
                                {t("posPage.searchModal.pagination", {
                                    count: searchTotal,
                                    page: searchPage + 1,
                                    totalPages: Math.ceil(
                                        searchTotal / ITEMS_PER_PAGE
                                    ),
                                })}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <button
                                    disabled={searchPage === 0}
                                    onClick={() =>
                                        setSearchPage((p) => Math.max(0, p - 1))
                                    }
                                    style={{
                                        padding: "6px 14px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "6px",
                                        backgroundColor:
                                            searchPage === 0
                                                ? "#f3f4f6"
                                                : "white",
                                        color:
                                            searchPage === 0
                                                ? "#9ca3af"
                                                : "#374151",
                                        fontSize: "0.8rem",
                                        fontWeight: 500,
                                        cursor:
                                            searchPage === 0
                                                ? "not-allowed"
                                                : "pointer",
                                    }}
                                >
                                    {t("common.previous")}
                                </button>
                                <button
                                    disabled={
                                        (searchPage + 1) * ITEMS_PER_PAGE >=
                                        searchTotal
                                    }
                                    onClick={() => setSearchPage((p) => p + 1)}
                                    style={{
                                        padding: "6px 14px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "6px",
                                        backgroundColor:
                                            (searchPage + 1) * ITEMS_PER_PAGE >=
                                            searchTotal
                                                ? "#f3f4f6"
                                                : "white",
                                        color:
                                            (searchPage + 1) * ITEMS_PER_PAGE >=
                                            searchTotal
                                                ? "#9ca3af"
                                                : "#374151",
                                        fontSize: "0.8rem",
                                        fontWeight: 500,
                                        cursor:
                                            (searchPage + 1) * ITEMS_PER_PAGE >=
                                            searchTotal
                                                ? "not-allowed"
                                                : "pointer",
                                    }}
                                >
                                    {t("common.next")}
                                </button>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* ═══ DISCOUNT MODAL ═══ */}
            <Dialog
                open={!!showDiscountModal}
                onClose={() => {
                    setShowDiscountModal(false)
                    setDiscountValue("")
                    setDiscountType("percentage")
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 1,
                    }}
                >
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
                        {showDiscountModal === "global"
                            ? t("posPage.discountModal.titleGlobal")
                            : t("posPage.discountModal.titleLine")}
                    </Typography>
                    <IconButton
                        onClick={() => {
                            setShowDiscountModal(false)
                            setDiscountValue("")
                            setDiscountType("percentage")
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color: "#374151",
                            mb: 0.5,
                        }}
                    >
                        {t("posPage.discountModal.amountLabel")}
                    </Typography>
                    <TextField
                        fullWidth
                        type="number"
                        value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                        autoFocus
                        sx={{ mb: 2 }}
                        inputProps={{ min: 0, step: "any" }}
                    />
                    <Typography
                        sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color: "#374151",
                            mb: 0.5,
                        }}
                    >
                        {t("posPage.discountModal.typeLabel")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                        <button
                            onClick={() => setDiscountType("percentage")}
                            style={{
                                flex: 1,
                                padding: "10px",
                                border: `2px solid ${
                                    discountType === "percentage"
                                        ? "#7f1d1d"
                                        : "#d1d5db"
                                }`,
                                borderRadius: "8px",
                                backgroundColor:
                                    discountType === "percentage"
                                        ? "#7f1d1d"
                                        : "white",
                                color:
                                    discountType === "percentage"
                                        ? "white"
                                        : "#374151",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                cursor: "pointer",
                            }}
                        >
                            {t("posPage.discountModal.percentage")}
                        </button>
                        <button
                            onClick={() => setDiscountType("amount")}
                            style={{
                                flex: 1,
                                padding: "10px",
                                border: `2px solid ${
                                    discountType === "amount"
                                        ? "#7f1d1d"
                                        : "#d1d5db"
                                }`,
                                borderRadius: "8px",
                                backgroundColor:
                                    discountType === "amount"
                                        ? "#7f1d1d"
                                        : "white",
                                color:
                                    discountType === "amount"
                                        ? "white"
                                        : "#374151",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                cursor: "pointer",
                            }}
                        >
                            {t("posPage.discountModal.amount")}
                        </button>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <button
                            onClick={() => {
                                setShowDiscountModal(false)
                                setDiscountValue("")
                                setDiscountType("percentage")
                            }}
                            style={{
                                flex: 1,
                                padding: "12px",
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                backgroundColor: "white",
                                color: "#374151",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                cursor: "pointer",
                            }}
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            disabled={
                                !discountValue || parseFloat(discountValue) < 0
                            }
                            onClick={() => {
                                const val = parseFloat(discountValue) || 0
                                const actualValue =
                                    discountType === "amount"
                                        ? Math.round(val * 100)
                                        : val
                                if (showDiscountModal === "global") {
                                    handleApplyGlobalDiscount(
                                        discountType,
                                        actualValue
                                    )
                                } else if (selectedLineId) {
                                    handleApplyLineDiscount(
                                        selectedLineId,
                                        discountType,
                                        actualValue
                                    )
                                }
                            }}
                            style={{
                                flex: 1,
                                padding: "12px",
                                border: "none",
                                borderRadius: "8px",
                                backgroundColor:
                                    !discountValue ||
                                    parseFloat(discountValue) < 0
                                        ? "#d1d5db"
                                        : "#7f1d1d",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                cursor:
                                    !discountValue ||
                                    parseFloat(discountValue) < 0
                                        ? "not-allowed"
                                        : "pointer",
                            }}
                        >
                            {t("common.apply")}
                        </button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* ═══ COMMENT MODAL ═══ */}
            <Dialog
                open={showCommentModal}
                onClose={() => setShowCommentModal(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 1,
                    }}
                >
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
                        {t("posPage.commentModal.title")}
                    </Typography>
                    <IconButton onClick={() => setShowCommentModal(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color: "#374151",
                            mb: 0.5,
                        }}
                    >
                        {t("posPage.commentModal.label")}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={5}
                        value={commentDraft}
                        onChange={(e) => {
                            if (e.target.value.length <= 250)
                                setCommentDraft(e.target.value)
                        }}
                        inputRef={commentModalInputRef}
                        sx={{ mb: 0.5 }}
                        inputProps={{ maxLength: 250 }}
                    />
                    <Typography
                        sx={{
                            fontSize: "0.7rem",
                            color: "#9ca3af",
                            textAlign: "right",
                            mb: 2,
                        }}
                    >
                        {t("posPage.commentModal.counter", {
                            count: commentDraft.length,
                        })}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <button
                            onClick={() => setShowCommentModal(false)}
                            style={{
                                flex: 1,
                                padding: "12px",
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                backgroundColor: "white",
                                color: "#374151",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                cursor: "pointer",
                            }}
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            onClick={handleUpdateComment}
                            style={{
                                flex: 1,
                                padding: "12px",
                                border: "none",
                                borderRadius: "8px",
                                backgroundColor: "#7f1d1d",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                cursor: "pointer",
                            }}
                        >
                            {t("common.save")}
                        </button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* ═══ NOUVEAU TICKET MODAL ═══ */}
            <Dialog
                open={showNewTicketModal}
                onClose={() => setShowNewTicketModal(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
                    {t("posPage.newTicketModal.title")}
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: "0.85rem", color: "#6b7280", mb: 2.5 }}
                    >
                        {t("posPage.newTicketModal.description")}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        <button
                            onClick={handleSaveTicket}
                            style={{
                                padding: "14px",
                                border: "none",
                                borderRadius: "8px",
                                backgroundColor: "#7f1d1d",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 8,
                            }}
                        >
                            <SaveIcon sx={{ fontSize: 18 }} />
                            {t("posPage.newTicketModal.saveCurrent")}
                        </button>
                        <button
                            onClick={() => {
                                setShowNewTicketModal(false)
                                clearPos()
                            }}
                            disabled={totalPaidMinor !== 0}
                            style={{
                                padding: "14px",
                                border: `1px solid ${
                                    payments.length > 0 ? "#e5e7eb" : "#fecaca"
                                }`,
                                borderRadius: "8px",
                                backgroundColor:
                                    totalPaidMinor !== 0
                                        ? "#f9fafb"
                                        : "#fff1f2",
                                color:
                                    totalPaidMinor !== 0
                                        ? "#9ca3af"
                                        : "#991b1b",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                cursor:
                                    totalPaidMinor !== 0
                                        ? "not-allowed"
                                        : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 8,
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                            {t("posPage.newTicketModal.resetCurrent")}
                        </button>
                        <button
                            onClick={() => setShowNewTicketModal(false)}
                            style={{
                                padding: "14px",
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                backgroundColor: "white",
                                color: "#374151",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                cursor: "pointer",
                            }}
                        >
                            {t("common.cancel")}
                        </button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* ═══ ANNULER TICKET MODAL ═══ */}
            <Dialog
                open={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent sx={{ textAlign: "left", pt: 3 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            mb: 2,
                        }}
                    >
                        <Box
                            sx={{
                                backgroundColor: "#fef3c7",
                                borderRadius: "50%",
                                width: 40,
                                height: 40,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <CancelIcon
                                sx={{ color: "#d97706", fontSize: 22 }}
                            />
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                    mb: 0.5,
                                }}
                            >
                                {t("posPage.cancelTicketModal.title")}
                            </Typography>
                            <Typography
                                sx={{ fontSize: "0.85rem", color: "#6b7280" }}
                            >
                                {t("posPage.cancelTicketModal.description")}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <button
                            onClick={() => setShowCancelModal(false)}
                            style={{
                                flex: 1,
                                padding: "12px",
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                backgroundColor: "white",
                                color: "#374151",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                cursor: "pointer",
                            }}
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            onClick={handleCancelTicket}
                            style={{
                                flex: 1,
                                padding: "12px",
                                border: "none",
                                borderRadius: "8px",
                                backgroundColor: "#dc2626",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                cursor: "pointer",
                            }}
                        >
                            {t("common.confirm")}
                        </button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* ═══ RECHERCHER TICKET MODAL ═══ */}
            <Dialog
                open={showTicketSearchModal}
                onClose={() => setShowTicketSearchModal(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 1,
                    }}
                >
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
                        {t("posPage.ticketSearchModal.title")}
                    </Typography>
                    <IconButton onClick={() => setShowTicketSearchModal(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        placeholder={String(
                            t("posPage.ticketSearchModal.placeholder")
                        )}
                        value={ticketSearchQuery}
                        onChange={(e) => handleSearchTickets(e.target.value)}
                        inputRef={ticketSearchModalInputRef}
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <SearchIcon sx={{ color: "#9ca3af", mr: 1 }} />
                            ),
                        }}
                    />
                    <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                        <thead>
                            <tr
                                style={{
                                    borderBottom: "2px solid #e5e7eb",
                                    textAlign: "left",
                                }}
                            >
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                    }}
                                >
                                    {t(
                                        "posPage.ticketSearchModal.columns.ticketNumber"
                                    )}
                                </th>
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                    }}
                                >
                                    {t("common.date")}
                                </th>
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                    }}
                                >
                                    {t("common.customer")}
                                </th>
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                        textAlign: "center",
                                    }}
                                >
                                    {t("common.items")}
                                </th>
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                        textAlign: "right",
                                    }}
                                >
                                    {t("common.total")}
                                </th>
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                        textAlign: "center",
                                    }}
                                >
                                    {t("common.status")}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {ticketList.map((ticket) => {
                                const paid = Number(
                                    ticket.total_paid_minor || 0
                                )
                                const total = Number(
                                    ticket.gross_total_minor || 0
                                )
                                const statusLabel =
                                    paid <= 0
                                        ? t(
                                              "posPage.ticketSearchModal.status.toPay"
                                          )
                                        : paid >= total
                                        ? t(
                                              "posPage.ticketSearchModal.status.paid"
                                          )
                                        : t(
                                              "posPage.ticketSearchModal.status.partial"
                                          )

                                const statusColor =
                                    paid <= 0
                                        ? "#dc2626"
                                        : paid >= total
                                        ? "#16a34a"
                                        : "#f59e0b"
                                const statusBg =
                                    paid <= 0
                                        ? "#fef2f2"
                                        : paid >= total
                                        ? "#f0fdf4"
                                        : "#fffbeb"
                                return (
                                    <tr
                                        key={ticket.id}
                                        onClick={() =>
                                            handleLoadTicket(ticket.id)
                                        }
                                        onMouseEnter={(e) => {
                                            (e.currentTarget as HTMLElement).style.backgroundColor = "#f5f3ff"
                                        }}
                                        onMouseLeave={(e) => {
                                            (e.currentTarget as HTMLElement).style.backgroundColor = ""
                                        }}
                                        style={{
                                            borderBottom: "1px solid #f3f4f6",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <td
                                            style={{
                                                padding: "10px 12px",
                                                fontSize: "0.85rem",
                                                color: "#4338ca",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {ticket.sale_number ||
                                                ticket.id.slice(0, 8)}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 12px",
                                                fontSize: "0.8rem",
                                                color: "#6b7280",
                                            }}
                                        >
                                            {new Date(
                                                ticket.updated_at
                                            ).toLocaleDateString(
                                                i18n.language || "fr-FR"
                                            )}
                                            <br />
                                            <span
                                                style={{ fontSize: "0.7rem" }}
                                            >
                                                {new Date(
                                                    ticket.updated_at
                                                ).toLocaleTimeString(
                                                    i18n.language || "fr-FR",
                                                    {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    }
                                                )}
                                            </span>
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 12px",
                                                fontSize: "0.8rem",
                                                color: "#6b7280",
                                            }}
                                        >
                                            {getCustomerDisplayName(
                                                ticket,
                                                String(
                                                    t(
                                                        "posPage.header.noCustomer"
                                                    )
                                                )
                                            )}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 12px",
                                                fontSize: "0.85rem",
                                                textAlign: "center",
                                            }}
                                        >
                                            {ticket.line_count ??
                                                String(t("common.dash"))}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 12px",
                                                fontSize: "0.9rem",
                                                fontWeight: 700,
                                                color: "#4338ca",
                                                textAlign: "right",
                                            }}
                                        >
                                            {formatMinor(
                                                ticket.gross_total_minor
                                            )}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 12px",
                                                textAlign: "center",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    display: "inline-block",
                                                    padding: "3px 8px",
                                                    borderRadius: "6px",
                                                    fontSize: "0.7rem",
                                                    fontWeight: 600,
                                                    backgroundColor: statusBg,
                                                    color: statusColor,
                                                }}
                                            >
                                                {statusLabel}
                                            </span>
                                        </td>
                                    </tr>
                                )
                            })}
                            {ticketList.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={6}
                                        style={{
                                            padding: "24px 12px",
                                            textAlign: "center",
                                            color: "#9ca3af",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        {t("posPage.ticketSearchModal.empty")}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </DialogContent>
            </Dialog>

            {/* ═══ EN ATTENTE MODAL ═══ */}
            <Dialog
                open={showOnHoldModal}
                onClose={() => setShowOnHoldModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 1,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                            sx={{ fontSize: "1.1rem", fontWeight: 700 }}
                        >
                            {t("posPage.onHoldModal.title")}
                        </Typography>
                        {ticketList.length > 0 && (
                            <Box
                                sx={{
                                    backgroundColor: "#10b981",
                                    color: "white",
                                    borderRadius: "50%",
                                    width: 24,
                                    height: 24,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                }}
                            >
                                {ticketList.length}
                            </Box>
                        )}
                    </Box>
                    <IconButton onClick={() => setShowOnHoldModal(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                        <thead>
                            <tr
                                style={{
                                    borderBottom: "2px solid #e5e7eb",
                                    textAlign: "left",
                                }}
                            >
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                    }}
                                >
                                    N° Ticket
                                </th>
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                    }}
                                >
                                    Date
                                </th>
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                    }}
                                >
                                    Client
                                </th>
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                        textAlign: "center",
                                    }}
                                >
                                    Articles
                                </th>
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                        textAlign: "right",
                                    }}
                                >
                                    {t("common.total")}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {ticketList.map((ticket) => (
                                <tr
                                    key={ticket.id}
                                    onClick={() => handleLoadTicket(ticket.id)}
                                    style={{
                                        borderBottom: "1px solid #f3f4f6",
                                        cursor: "pointer",
                                    }}
                                >
                                    <td
                                        style={{
                                            padding: "10px 12px",
                                            fontSize: "0.85rem",
                                            color: "#4338ca",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {ticket.sale_number ||
                                            ticket.id.slice(0, 8)}
                                    </td>
                                    <td
                                        style={{
                                            padding: "10px 12px",
                                            fontSize: "0.8rem",
                                            color: "#6b7280",
                                        }}
                                    >
                                        {new Date(
                                            ticket.updated_at
                                        ).toLocaleDateString(
                                            i18n.language || "fr-FR"
                                        )}
                                        <br />
                                        <span style={{ fontSize: "0.7rem" }}>
                                            {new Date(
                                                ticket.updated_at
                                            ).toLocaleTimeString(
                                                i18n.language || "fr-FR",
                                                {
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                }
                                            )}
                                        </span>
                                    </td>
                                    <td
                                        style={{
                                            padding: "10px 12px",
                                            fontSize: "0.8rem",
                                            color: "#6b7280",
                                        }}
                                    >
                                        {getCustomerDisplayName(
                                            ticket,
                                            String(
                                                t("posPage.header.noCustomer")
                                            )
                                        )}
                                    </td>
                                    <td
                                        style={{
                                            padding: "10px 12px",
                                            fontSize: "0.85rem",
                                            textAlign: "center",
                                        }}
                                    >
                                        {ticket.line_count ??
                                            String(t("common.dash"))}
                                    </td>
                                    <td
                                        style={{
                                            padding: "10px 12px",
                                            fontSize: "0.9rem",
                                            fontWeight: 700,
                                            color: "#4338ca",
                                            textAlign: "right",
                                        }}
                                    >
                                        {formatMinor(ticket.gross_total_minor)}
                                    </td>
                                </tr>
                            ))}
                            {ticketList.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={5}
                                        style={{
                                            padding: "24px 12px",
                                            textAlign: "center",
                                            color: "#9ca3af",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        {t("posPage.onHoldModal.empty")}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </DialogContent>
            </Dialog>

            {/* ═══ HISTORIQUE REGLEMENTS MODAL ═══ */}
            <Dialog
                open={showPaymentHistoryModal}
                onClose={() => setShowPaymentHistoryModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 1,
                    }}
                >
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
                        {t("posPage.paymentHistoryModal.title")}
                    </Typography>
                    <IconButton
                        onClick={() => setShowPaymentHistoryModal(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                        <thead>
                            <tr
                                style={{
                                    borderBottom: "2px solid #e5e7eb",
                                    textAlign: "left",
                                }}
                            >
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                    }}
                                >
                                    {t(
                                        "posPage.paymentHistoryModal.columns.dateTime"
                                    )}
                                </th>
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                    }}
                                >
                                    {t(
                                        "posPage.paymentHistoryModal.columns.paymentType"
                                    )}
                                </th>
                                <th
                                    style={{
                                        padding: "8px 12px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                        textAlign: "right",
                                    }}
                                >
                                    {t("common.amount")}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.map((p) => {
                                const method = paymentMethods.find(
                                    (m) => m.id === p.payment_method_id
                                )
                                return (
                                    <tr
                                        key={p.id}
                                        style={{
                                            borderBottom: "1px solid #f3f4f6",
                                        }}
                                    >
                                        <td
                                            style={{
                                                padding: "10px 12px",
                                                fontSize: "0.8rem",
                                                color: "#6b7280",
                                            }}
                                        >
                                            {new Date(
                                                p.paid_at
                                            ).toLocaleDateString(
                                                i18n.language || "fr-FR"
                                            )}
                                            <br />
                                            <span
                                                style={{ fontSize: "0.7rem" }}
                                            >
                                                {new Date(
                                                    p.paid_at
                                                ).toLocaleTimeString(
                                                    i18n.language || "fr-FR"
                                                )}
                                            </span>
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 12px",
                                                fontSize: "0.85rem",
                                                fontWeight: 500,
                                            }}
                                        >
                                            {method?.label?.toUpperCase() ||
                                                "—"}
                                        </td>
                                        <td
                                            style={{
                                                padding: "10px 12px",
                                                fontSize: "0.9rem",
                                                fontWeight: 700,
                                                color:
                                                    Number(p.amount_minor) < 0
                                                        ? "#dc2626"
                                                        : "#10b981",
                                                textAlign: "right",
                                            }}
                                        >
                                            {Number(p.amount_minor) < 0
                                                ? `- ${formatMinor(
                                                      Math.abs(
                                                          Number(p.amount_minor)
                                                      )
                                                  )}`
                                                : `+ ${formatMinor(
                                                      p.amount_minor
                                                  )}`}
                                        </td>
                                    </tr>
                                )
                            })}
                            {payments.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={3}
                                        style={{
                                            padding: "24px 12px",
                                            textAlign: "center",
                                            color: "#9ca3af",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        {t("posPage.paymentHistoryModal.empty")}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        {payments.length > 0 && (
                            <tfoot>
                                <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                                    <td
                                        colSpan={2}
                                        style={{
                                            padding: "10px 12px",
                                            fontSize: "0.85rem",
                                            fontWeight: 700,
                                        }}
                                    >
                                        {t("posPage.paymentHistoryModal.total")}
                                    </td>
                                    <td
                                        style={{
                                            padding: "10px 12px",
                                            fontSize: "0.95rem",
                                            fontWeight: 700,
                                            color: "#4338ca",
                                            textAlign: "right",
                                        }}
                                    >
                                        {formatMinor(totalPaidMinor)}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </DialogContent>
            </Dialog>

            {/* ═══ ANNULER RÈGLEMENT MODAL ═══ */}
            <Dialog
                open={showReversePaymentModal}
                onClose={() => setShowReversePaymentModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 1,
                    }}
                >
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
                        {t("posPage.reversePaymentModal.title")}
                    </Typography>
                    <IconButton
                        onClick={() => setShowReversePaymentModal(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: "0.85rem", color: "#6b7280", mb: 2 }}
                    >
                        {t("posPage.reversePaymentModal.description")}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {payments
                            .filter((p) => Number(p.amount_minor) > 0)
                            .map((p) => {
                                const method = paymentMethods.find(
                                    (m) => m.id === p.payment_method_id
                                )
                                const isReversed = payments.some(
                                    (r) => r.reverses_payment_id === p.id
                                )
                                return (
                                    <button
                                        key={p.id}
                                        disabled={isReversed || loading}
                                        onClick={() =>
                                            handleReversePayment(p.id)
                                        }
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            padding: "12px 16px",
                                            border: isReversed
                                                ? "1px solid #e5e7eb"
                                                : "1px solid #fecaca",
                                            borderRadius: "8px",
                                            backgroundColor: isReversed
                                                ? "#f9fafb"
                                                : "#fff1f2",
                                            cursor: isReversed
                                                ? "not-allowed"
                                                : "pointer",
                                            opacity: isReversed ? 0.5 : 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "0.85rem",
                                                    fontWeight: 600,
                                                    color: isReversed
                                                        ? "#9ca3af"
                                                        : "#374151",
                                                }}
                                            >
                                                {method?.label?.toUpperCase() ||
                                                    "—"}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: "0.7rem",
                                                    color: "#9ca3af",
                                                }}
                                            >
                                                {new Date(
                                                    p.paid_at
                                                ).toLocaleDateString(
                                                    i18n.language || "fr-FR"
                                                )}{" "}
                                                {new Date(
                                                    p.paid_at
                                                ).toLocaleTimeString(
                                                    i18n.language || "fr-FR",
                                                    {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    }
                                                )}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "0.95rem",
                                                    fontWeight: 700,
                                                    color: isReversed
                                                        ? "#9ca3af"
                                                        : "#dc2626",
                                                }}
                                            >
                                                {formatMinor(p.amount_minor)}
                                            </Typography>
                                            {isReversed && (
                                                <Typography
                                                    sx={{
                                                        fontSize: "0.65rem",
                                                        fontWeight: 600,
                                                        color: "#9ca3af",
                                                        backgroundColor:
                                                            "#f3f4f6",
                                                        padding: "2px 6px",
                                                        borderRadius: "4px",
                                                    }}
                                                >
                                                    {t(
                                                        "posPage.reversePaymentModal.reversed"
                                                    )}
                                                </Typography>
                                            )}
                                        </Box>
                                    </button>
                                )
                            })}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* ═══ RECHERCHER UN CLIENT MODAL ═══ */}
            <Dialog
                open={showCustomerModal}
                onClose={() => setShowCustomerModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 1,
                    }}
                >
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
                        {t("posPage.customerModal.title")}
                    </Typography>
                    <IconButton onClick={() => setShowCustomerModal(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            border: "2px solid #e5e7eb",
                            borderRadius: "10px",
                            px: 1.5,
                            mb: 2,
                            "&:focus-within": { borderColor: "#7f1d1d" },
                        }}
                    >
                        <SearchIcon sx={{ color: "#9ca3af", mr: 1 }} />
                        <input
                            type="text"
                            placeholder={String(
                                t("posPage.customerModal.placeholder")
                            )}
                            value={customerSearch}
                            onChange={(e) =>
                                handleSearchCustomers(e.target.value)
                            }
                            autoFocus
                            style={{
                                flex: 1,
                                border: "none",
                                outline: "none",
                                padding: "12px 0",
                                fontSize: "0.9rem",
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        {customerList.map((c) => (
                            <button
                                key={c.id}
                                onClick={async () => {
                                    await handleSelectCustomer(c)
                                    setShowCustomerModal(false)
                                }}
                                style={{
                                    width: "100%",
                                    padding: "16px 20px",
                                    textAlign: "left",
                                    backgroundColor: "white",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "12px",
                                    cursor: "pointer",
                                }}
                            >
                                <div
                                    style={{
                                        fontWeight: 700,
                                        fontSize: "0.9rem",
                                        color: "#1e1b4b",
                                    }}
                                >
                                    {getCustomerDisplayName(c)}
                                </div>
                                {c.email && (
                                    <div
                                        style={{
                                            fontSize: "0.8rem",
                                            color: "#6b7280",
                                            marginTop: 3,
                                        }}
                                    >
                                        {c.email}
                                    </div>
                                )}
                                {c.phone && (
                                    <div
                                        style={{
                                            fontSize: "0.8rem",
                                            color: "#6b7280",
                                            marginTop: 1,
                                        }}
                                    >
                                        {c.phone}
                                    </div>
                                )}
                            </button>
                        ))}
                        {customerList.length === 0 && customerSearch && (
                            <Typography
                                sx={{
                                    textAlign: "center",
                                    color: "#9ca3af",
                                    fontSize: "0.85rem",
                                    py: 2,
                                }}
                            >
                                {t("posPage.customerModal.empty")}
                            </Typography>
                        )}
                    </Box>

                    <button
                        onClick={async () => {
                            await handleClearSelectedCustomer()
                            setShowCustomerModal(false)
                        }}
                        style={{
                            width: "100%",
                            padding: "14px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "white",
                            color: "#1f2937",
                            border: "1px solid #e5e7eb",
                            borderRadius: "10px",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            cursor: "pointer",
                        }}
                    >
                        {t("posPage.customerModal.continueWithoutCustomer")}
                    </button>
                </DialogContent>
            </Dialog>

            {/* ═══ PAIEMENT TERMINÉ MODAL ═══ */}
            <Dialog
                open={showFinalizeModal}
                onClose={() => {
                    setShowFinalizeModal(false)
                    clearPos()
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{ fontWeight: 700, fontSize: "1.1rem", pb: 0 }}
                >
                    {t("posPage.finalizeModal.title")}
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Box
                        sx={{
                            backgroundColor: "#ecfdf5",
                            border: "1px solid #a7f3d0",
                            borderRadius: "10px",
                            py: 2,
                            px: 3,
                            textAlign: "center",
                            mb: 2.5,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "0.8rem",
                                color: "#065f46",
                                mb: 0.5,
                            }}
                        >
                            {t("posPage.finalizeModal.totalCollected")}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "1.6rem",
                                fontWeight: 700,
                                color: "#059669",
                            }}
                        >
                            {formatMinor(totalPaidMinor)}
                        </Typography>
                    </Box>

                    <Typography
                        sx={{
                            textAlign: "center",
                            fontSize: "0.85rem",
                            color: "#374151",
                            mb: 1.5,
                        }}
                    >
                        {t("posPage.finalizeModal.askReceipt")}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        <button
                            onClick={handlePrintTicket}
                            disabled={!currentReceipt?.id}
                            style={{
                                width: "100%",
                                padding: "14px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                                backgroundColor: "#7f1d1d",
                                color: "white",
                                border: "none",
                                borderRadius: "10px",
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                cursor: currentReceipt?.id
                                    ? "pointer"
                                    : "not-allowed",
                                opacity: currentReceipt?.id ? 1 : 0.5,
                            }}
                        >
                            <PrintIcon sx={{ fontSize: 20 }} />
                            {t("posPage.finalizeModal.printReceipt")}
                        </button>
                        <button
                            onClick={handleSendByEmail}
                            disabled={!currentReceipt?.id}
                            style={{
                                width: "100%",
                                padding: "14px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                                backgroundColor: "white",
                                color: "#374151",
                                border: "1px solid #e5e7eb",
                                borderRadius: "10px",
                                fontSize: "0.85rem",
                                fontWeight: 500,
                                cursor: currentReceipt?.id
                                    ? "pointer"
                                    : "not-allowed",
                                opacity: currentReceipt?.id ? 1 : 0.5,
                            }}
                        >
                            <EmailIcon
                                sx={{ fontSize: 20, color: "#6b7280" }}
                            />
                            {t("posPage.finalizeModal.sendByEmail")}
                        </button>
                        <button
                            onClick={handleShowQrCode}
                            disabled={!currentReceipt?.id}
                            style={{
                                width: "100%",
                                padding: "14px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                                backgroundColor: "white",
                                color: "#374151",
                                border: "1px solid #e5e7eb",
                                borderRadius: "10px",
                                fontSize: "0.85rem",
                                fontWeight: 500,
                                cursor: currentReceipt?.id
                                    ? "pointer"
                                    : "not-allowed",
                                opacity: currentReceipt?.id ? 1 : 0.5,
                            }}
                        >
                            <QrCode2Icon
                                sx={{ fontSize: 20, color: "#6b7280" }}
                            />
                            {t("posPage.finalizeModal.showQrCode")}
                        </button>
                        <button
                            onClick={() => {
                                setShowFinalizeModal(false)
                                clearPos()
                            }}
                            style={{
                                width: "100%",
                                padding: "14px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                                backgroundColor: "white",
                                color: "#6b7280",
                                border: "1px solid #e5e7eb",
                                borderRadius: "10px",
                                fontSize: "0.85rem",
                                fontWeight: 500,
                                cursor: "pointer",
                            }}
                        >
                            <CloseIcon
                                sx={{ fontSize: 20, color: "#9ca3af" }}
                            />
                            {t("posPage.finalizeModal.noReceipt")}
                        </button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* ═══ EMAIL INPUT MODAL ═══ */}
            <Dialog
                open={showEmailInputModal}
                onClose={() => setShowEmailInputModal(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 0 }}>
                    {t("posPage.finalizeModal.sendByEmail")}
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Typography
                        sx={{
                            fontSize: "0.85rem",
                            color: "#374151",
                            mb: 2,
                        }}
                    >
                        {t("posPage.finalizeModal.emailPrompt")}
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        type="email"
                        label={t("posPage.finalizeModal.emailLabel")}
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleConfirmEmailSend()
                        }}
                        size="small"
                        disabled={emailSending}
                    />
                    {emailSuccess && (
                        <Alert severity="success" sx={{ mt: 1.5 }}>
                            {t("posPage.finalizeModal.emailSent")}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setShowEmailInputModal(false)}
                        color="inherit"
                        disabled={emailSending}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        onClick={handleConfirmEmailSend}
                        variant="contained"
                        disabled={emailSending || !emailInput}
                        sx={{ backgroundColor: "#7f1d1d" }}
                    >
                        {emailSending
                            ? t("posPage.finalizeModal.sending")
                            : t("posPage.finalizeModal.send")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ═══ QR CODE MODAL ═══ */}
            <Dialog
                open={showQrModal}
                onClose={() => setShowQrModal(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        pb: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    {t("posPage.finalizeModal.showQrCode")}
                    <IconButton
                        onClick={() => setShowQrModal(false)}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2, textAlign: "center" }}>
                    <Typography
                        sx={{
                            fontSize: "0.85rem",
                            color: "#374151",
                            mb: 2,
                        }}
                    >
                        {t("posPage.finalizeModal.qrCodePrompt")}
                    </Typography>
                    {qrDataUrl && (
                        <Box
                            sx={{
                                display: "inline-block",
                                p: 1.5,
                                border: "1px solid #e5e7eb",
                                borderRadius: "10px",
                                mb: 1,
                            }}
                        >
                            <img
                                src={qrDataUrl}
                                alt="QR Code"
                                style={{
                                    display: "block",
                                    width: 240,
                                    height: 240,
                                }}
                            />
                        </Box>
                    )}
                    <Typography
                        sx={{
                            fontSize: "0.75rem",
                            color: "#9ca3af",
                            mt: 1,
                            wordBreak: "break-all",
                        }}
                    >
                        {currentReceipt?.id
                            ? documentsService.getPublicReceiptUrl(
                                  currentReceipt.id
                              )
                            : ""}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setShowQrModal(false)}
                        variant="outlined"
                        color="inherit"
                    >
                        {t("common.close")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ═══ AUTRES MODES DE PAIEMENT MODAL ═══ */}
            <Dialog
                open={showOtherPayments}
                onClose={() => setShowOtherPayments(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 0,
                    }}
                >
                    <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>
                        {t("posPage.payment.otherMethods")}
                    </Typography>
                    <IconButton onClick={() => setShowOtherPayments(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {paymentMethods.slice(3).map((m) => (
                            <button
                                key={m.id}
                                onClick={() => {
                                    setSelectedPaymentMethodId(m.id)
                                    setDisplayValue(
                                        (remainingMinor / 100).toFixed(2)
                                    )
                                    setDisplayDirty(false)
                                    setShowOtherPayments(false)
                                }}
                                style={{
                                    width: "100%",
                                    padding: "16px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                    backgroundColor: "white",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "10px",
                                    fontSize: "0.85rem",
                                    fontWeight: 500,
                                    color: "#1f2937",
                                    cursor: "pointer",
                                }}
                            >
                                {getPaymentMethodIcon(
                                    m.type || m.payment_type,
                                    {
                                        fontSize: 20,
                                        color: "#4b5563",
                                    }
                                )}
                                {m.label}
                            </button>
                        ))}
                        {paymentMethods.length <= 3 && (
                            <Typography
                                sx={{
                                    textAlign: "center",
                                    color: "#9ca3af",
                                    fontSize: "0.85rem",
                                    py: 2,
                                }}
                            >
                                {t("posPage.otherPaymentsModal.empty")}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={showMovementsModal}
                onClose={() => setShowMovementsModal(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 1,
                    }}
                >
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
                        {t("posPage.movements.title", { registerName })}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <button
                            onClick={() => {
                                setMovementDirection("in")
                                setMovementPaymentMethod("cash")
                                setMovementAmountInput("")
                                setMovementChequeCountInput("")
                                setMovementCommentInput("")
                                setShowAddMovementModal(true)
                            }}
                            style={{
                                padding: "8px 12px",
                                border: "none",
                                borderRadius: "8px",
                                backgroundColor: "#7f1d1d",
                                color: "white",
                                fontWeight: 600,
                                cursor: "pointer",
                                fontSize: "0.8rem",
                            }}
                        >
                            {t("posPage.movements.addAction")}
                        </button>
                        <IconButton
                            onClick={() => setShowMovementsModal(false)}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ color: "#6b7280", fontSize: "0.78rem", mb: 1 }}
                    >
                        Affichage limité aux {MOVEMENTS_LOOKBACK_DAYS} derniers
                        jours.
                    </Typography>
                    {sessionMovementsLoading ? (
                        <Typography sx={{ color: "#6b7280", py: 2 }}>
                            {t("common.loading")}
                        </Typography>
                    ) : groupedMovementsBySession.length === 0 ? (
                        <Typography sx={{ color: "#6b7280", py: 2 }}>
                            {t("posPage.movements.empty")}
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            {groupedMovementsBySession.map((group) => (
                                <Box
                                    key={group.sessionId}
                                    sx={{
                                        border: "1px solid #e5e7eb",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: "#f9fafb",
                                            px: 2,
                                            py: 1,
                                            fontSize: "0.8rem",
                                            fontWeight: 700,
                                            color: "#374151",
                                        }}
                                    >
                                        N° clôture : {group.sessionId}
                                    </Box>
                                    <Box sx={{ overflowX: "auto" }}>
                                        <table
                                            style={{
                                                width: "100%",
                                                borderCollapse: "collapse",
                                                minWidth: 980,
                                            }}
                                        >
                                            <thead>
                                                <tr
                                                    style={{
                                                        backgroundColor:
                                                            "#f9fafb",
                                                    }}
                                                >
                                                    {[
                                                        {
                                                            key: "dateTime",
                                                            value: t(
                                                                "posPage.movements.columns.dateTime"
                                                            ),
                                                        },
                                                        {
                                                            key: "type",
                                                            value: t(
                                                                "posPage.movements.columns.type"
                                                            ),
                                                        },
                                                        {
                                                            key: "comment",
                                                            value: t(
                                                                "posPage.movements.columns.comment"
                                                            ),
                                                        },
                                                        {
                                                            key: "seller",
                                                            value: t(
                                                                "posPage.movements.columns.seller"
                                                            ),
                                                        },
                                                        {
                                                            key: "paymentMethod",
                                                            value: t(
                                                                "posPage.movements.columns.paymentMethod"
                                                            ),
                                                        },
                                                        {
                                                            key: "theoreticalAmount",
                                                            value: t(
                                                                "posPage.movements.columns.theoreticalAmount"
                                                            ),
                                                        },
                                                        {
                                                            key: "countedAmount",
                                                            value: t(
                                                                "posPage.movements.columns.countedAmount"
                                                            ),
                                                        },
                                                        {
                                                            key: "difference",
                                                            value: t(
                                                                "posPage.movements.columns.difference"
                                                            ),
                                                        },
                                                        {
                                                            key: "amount",
                                                            value: t(
                                                                "common.amount"
                                                            ),
                                                        },
                                                    ].map((column) => (
                                                        <th
                                                            key={column.key}
                                                            style={{
                                                                padding:
                                                                    "8px 10px",
                                                                fontSize:
                                                                    "0.68rem",
                                                                textTransform:
                                                                    "uppercase",
                                                                color: "#6b7280",
                                                                textAlign:
                                                                    column.key ===
                                                                        "amount" ||
                                                                    column.key ===
                                                                        "difference" ||
                                                                    column.key ===
                                                                        "theoreticalAmount" ||
                                                                    column.key ===
                                                                        "countedAmount"
                                                                        ? "right"
                                                                        : "left",
                                                                borderBottom:
                                                                    "1px solid #e5e7eb",
                                                            }}
                                                        >
                                                            {column.value}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.movements.map(
                                                    (movement: any) => {
                                                        const movementType =
                                                            normalizePaymentMethodType(
                                                                movement.payment_method_type
                                                            )
                                                        const movementMethod =
                                                            paymentMethods.find(
                                                                (m) =>
                                                                    m.id ===
                                                                    movement.payment_method_id
                                                            ) ||
                                                            paymentMethods.find(
                                                                (m) =>
                                                                    normalizePaymentMethodType(
                                                                        m.type ||
                                                                            m.payment_type ||
                                                                            m.code
                                                                    ) ===
                                                                    movementType
                                                            )
                                                        const movementMethodLabel =
                                                            movementMethod?.label ||
                                                            getPaymentMethodTypeLabel(
                                                                movementType,
                                                                t
                                                            )
                                                        const movementMethodTypeForBadge =
                                                            normalizePaymentMethodType(
                                                                movementMethod?.type ||
                                                                    movementMethod?.payment_type ||
                                                                    movementType
                                                            )
                                                        const isOut =
                                                            String(
                                                                movement.direction ||
                                                                    ""
                                                            ).toLowerCase() ===
                                                            "out"
                                                        return (
                                                            <tr
                                                                key={
                                                                    movement.id
                                                                }
                                                                style={{
                                                                    borderBottom:
                                                                        "1px solid #f3f4f6",
                                                                }}
                                                            >
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            "8px 10px",
                                                                        fontSize:
                                                                            "0.78rem",
                                                                        color: "#374151",
                                                                    }}
                                                                >
                                                                    {new Date(
                                                                        movement.created_at
                                                                    ).toLocaleDateString(
                                                                        i18n.language ||
                                                                            "fr-FR"
                                                                    )}{" "}
                                                                    {new Date(
                                                                        movement.created_at
                                                                    ).toLocaleTimeString(
                                                                        i18n.language ||
                                                                            "fr-FR"
                                                                    )}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            "8px 10px",
                                                                        fontSize:
                                                                            "0.78rem",
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    {getMovementTypeLabel(
                                                                        movement,
                                                                        t
                                                                    )}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            "8px 10px",
                                                                        fontSize:
                                                                            "0.78rem",
                                                                        color: "#4b5563",
                                                                    }}
                                                                >
                                                                    {getMovementCommentLabel(
                                                                        movement,
                                                                        t
                                                                    )}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            "8px 10px",
                                                                        fontSize:
                                                                            "0.75rem",
                                                                        color: "#6b7280",
                                                                    }}
                                                                >
                                                                    {movement.created_by_user_display_name ||
                                                                        movement.created_by_user_id ||
                                                                        "—"}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            "8px 10px",
                                                                        fontSize:
                                                                            "0.75rem",
                                                                        color: "#6b7280",
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={getPaymentMethodBadgeStyle(
                                                                            movementMethodTypeForBadge
                                                                        )}
                                                                    >
                                                                        {
                                                                            movementMethodLabel
                                                                        }
                                                                    </span>
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            "8px 10px",
                                                                        fontSize:
                                                                            "0.78rem",
                                                                        textAlign:
                                                                            "right",
                                                                    }}
                                                                >
                                                                    {movement.theoretical_minor ==
                                                                    null
                                                                        ? "—"
                                                                        : formatMinor(
                                                                              movement.theoretical_minor
                                                                          )}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            "8px 10px",
                                                                        fontSize:
                                                                            "0.78rem",
                                                                        textAlign:
                                                                            "right",
                                                                    }}
                                                                >
                                                                    {movement.counted_minor ==
                                                                    null
                                                                        ? "—"
                                                                        : formatMinor(
                                                                              movement.counted_minor
                                                                          )}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            "8px 10px",
                                                                        fontSize:
                                                                            "0.78rem",
                                                                        textAlign:
                                                                            "right",
                                                                        color:
                                                                            movement.discrepancy_minor ==
                                                                            null
                                                                                ? "#6b7280"
                                                                                : Number(
                                                                                      movement.discrepancy_minor
                                                                                  ) ===
                                                                                  0
                                                                                ? "#374151"
                                                                                : "#dc2626",
                                                                        fontWeight:
                                                                            movement.discrepancy_minor ==
                                                                            null
                                                                                ? 400
                                                                                : 600,
                                                                    }}
                                                                >
                                                                    {movement.discrepancy_minor ==
                                                                    null
                                                                        ? "—"
                                                                        : formatMinor(
                                                                              movement.discrepancy_minor
                                                                          )}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding:
                                                                            "8px 10px",
                                                                        fontSize:
                                                                            "0.82rem",
                                                                        fontWeight: 700,
                                                                        textAlign:
                                                                            "right",
                                                                        color: isOut
                                                                            ? "#dc2626"
                                                                            : "#10b981",
                                                                    }}
                                                                >
                                                                    {isOut
                                                                        ? "−"
                                                                        : "+"}{" "}
                                                                    {formatMinor(
                                                                        movement.amount_minor
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )
                                                    }
                                                )}
                                            </tbody>
                                        </table>
                                    </Box>
                                </Box>
                            ))}
                            {registerMovementsHasMore && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        pt: 1,
                                    }}
                                >
                                    <button
                                        onClick={
                                            handleLoadMoreRegisterMovements
                                        }
                                        disabled={registerMovementsLoadingMore}
                                        style={{
                                            padding: "8px 14px",
                                            borderRadius: "8px",
                                            border: "1px solid #d1d5db",
                                            backgroundColor: "#fff",
                                            color: "#374151",
                                            fontWeight: 600,
                                            cursor: registerMovementsLoadingMore
                                                ? "not-allowed"
                                                : "pointer",
                                            opacity:
                                                registerMovementsLoadingMore
                                                    ? 0.7
                                                    : 1,
                                        }}
                                    >
                                        {registerMovementsLoadingMore
                                            ? "Chargement..."
                                            : "Charger plus de mouvements"}
                                    </button>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={showAddMovementModal}
                onClose={() => setShowAddMovementModal(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {t("posPage.addMovementModal.title")}
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            px: 1.5,
                            py: 1.25,
                            mb: 2,
                            backgroundColor: "#f9fafb",
                        }}
                    >
                        <Typography
                            sx={{ fontSize: "0.75rem", color: "#6b7280" }}
                        >
                            {t("posPage.addMovementModal.availableBefore")}
                        </Typography>
                        <Typography
                            sx={{ fontSize: "0.85rem", fontWeight: 600 }}
                        >
                            {t("common.paymentMethods.cash")}:{" "}
                            {formatMinor(availableFundMinor)}
                        </Typography>
                        <Typography
                            sx={{ fontSize: "0.85rem", fontWeight: 600 }}
                        >
                            {t("common.paymentMethods.check")}:{" "}
                            {Math.max(0, availableChequeCount)} (
                            {formatMinor(availableChequeMinor)})
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.75rem",
                                mt: 0.25,
                                color:
                                    movementAfterMinor < 0 ||
                                    movementAfterChequeCount < 0
                                        ? "#dc2626"
                                        : "#6b7280",
                            }}
                        >
                            {t("posPage.addMovementModal.afterMovement")}
                            {movementPaymentMethod === "cash"
                                ? ` ${t(
                                      "common.paymentMethods.cash"
                                  )} ${formatMinor(movementAfterMinor)} | ${t(
                                      "common.paymentMethods.check"
                                  )} ${Math.max(
                                      0,
                                      availableChequeCount
                                  )} (${formatMinor(availableChequeMinor)})`
                                : ` ${t(
                                      "common.paymentMethods.cash"
                                  )} ${formatMinor(availableFundMinor)} | ${t(
                                      "common.paymentMethods.check"
                                  )} ${Math.max(
                                      0,
                                      movementAfterChequeCount
                                  )} (${formatMinor(movementAfterMinor)})`}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                        <button
                            onClick={() => setMovementPaymentMethod("cash")}
                            style={{
                                flex: 1,
                                padding: "10px",
                                borderRadius: "8px",
                                border: `1px solid ${
                                    movementPaymentMethod === "cash"
                                        ? "#10b981"
                                        : "#d1d5db"
                                }`,
                                backgroundColor:
                                    movementPaymentMethod === "cash"
                                        ? "#ecfdf5"
                                        : "white",
                                color:
                                    movementPaymentMethod === "cash"
                                        ? "#047857"
                                        : "#374151",
                                fontWeight: 700,
                                cursor: "pointer",
                            }}
                        >
                            {t("common.paymentMethods.cash")}
                        </button>
                        <button
                            onClick={() => setMovementPaymentMethod("cheque")}
                            style={{
                                flex: 1,
                                padding: "10px",
                                borderRadius: "8px",
                                border: `1px solid ${
                                    movementPaymentMethod === "cheque"
                                        ? "#2563eb"
                                        : "#d1d5db"
                                }`,
                                backgroundColor:
                                    movementPaymentMethod === "cheque"
                                        ? "#eff6ff"
                                        : "white",
                                color:
                                    movementPaymentMethod === "cheque"
                                        ? "#1d4ed8"
                                        : "#374151",
                                fontWeight: 700,
                                cursor: "pointer",
                            }}
                        >
                            {t("common.paymentMethods.check")}
                        </button>
                    </Box>

                    <TextField
                        fullWidth
                        type="number"
                        label={
                            movementPaymentMethod === "cheque"
                                ? movementDirection === "in"
                                    ? String(
                                          t(
                                              "posPage.addMovementModal.amount.checkIn"
                                          )
                                      )
                                    : String(
                                          t(
                                              "posPage.addMovementModal.amount.checkOut"
                                          )
                                      )
                                : movementDirection === "in"
                                ? String(
                                      t(
                                          "posPage.addMovementModal.amount.cashIn"
                                      )
                                  )
                                : String(
                                      t(
                                          "posPage.addMovementModal.amount.cashOut"
                                      )
                                  )
                        }
                        value={movementAmountInput}
                        onChange={(e) => setMovementAmountInput(e.target.value)}
                        inputProps={{ min: 0, step: "0.01" }}
                        sx={{ mb: 2 }}
                        autoFocus
                    />

                    {movementPaymentMethod === "cheque" ? (
                        <TextField
                            fullWidth
                            type="number"
                            label={
                                movementDirection === "in"
                                    ? String(
                                          t(
                                              "posPage.addMovementModal.checkCount.in"
                                          )
                                      )
                                    : String(
                                          t(
                                              "posPage.addMovementModal.checkCount.out"
                                          )
                                      )
                            }
                            value={movementChequeCountInput}
                            onChange={(e) =>
                                setMovementChequeCountInput(e.target.value)
                            }
                            inputProps={{ min: 1, step: "1" }}
                            sx={{ mb: 2 }}
                        />
                    ) : null}

                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                        <button
                            onClick={() => setMovementDirection("in")}
                            style={{
                                flex: 1,
                                padding: "10px",
                                borderRadius: "8px",
                                border: `1px solid ${
                                    movementDirection === "in"
                                        ? "#10b981"
                                        : "#d1d5db"
                                }`,
                                backgroundColor:
                                    movementDirection === "in"
                                        ? "#ecfdf5"
                                        : "white",
                                color:
                                    movementDirection === "in"
                                        ? "#047857"
                                        : "#374151",
                                fontWeight: 700,
                                cursor: "pointer",
                            }}
                        >
                            {t("posPage.addMovementModal.direction.in")}
                        </button>
                        <button
                            onClick={() => setMovementDirection("out")}
                            style={{
                                flex: 1,
                                padding: "10px",
                                borderRadius: "8px",
                                border: `1px solid ${
                                    movementDirection === "out"
                                        ? "#dc2626"
                                        : "#d1d5db"
                                }`,
                                backgroundColor:
                                    movementDirection === "out"
                                        ? "#fef2f2"
                                        : "white",
                                color:
                                    movementDirection === "out"
                                        ? "#b91c1c"
                                        : "#374151",
                                fontWeight: 700,
                                cursor: "pointer",
                            }}
                        >
                            {t("posPage.addMovementModal.direction.out")}
                        </button>
                    </Box>
                    <TextField
                        fullWidth
                        label={String(
                            t("posPage.addMovementModal.commentLabel")
                        )}
                        value={movementCommentInput}
                        onChange={(e) =>
                            setMovementCommentInput(e.target.value)
                        }
                        placeholder={
                            movementDirection === "in"
                                ? String(
                                      t(
                                          "posPage.addMovementModal.commentPlaceholderIn"
                                      )
                                  )
                                : String(
                                      t(
                                          "posPage.addMovementModal.commentPlaceholderOut"
                                      )
                                  )
                        }
                        sx={{ mb: 2 }}
                    />

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                        }}
                    >
                        <button
                            onClick={() => setShowAddMovementModal(false)}
                            style={{
                                padding: "10px 14px",
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                backgroundColor: "white",
                                color: "#374151",
                                fontWeight: 600,
                                cursor: "pointer",
                            }}
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            onClick={handleSubmitMovement}
                            disabled={loading}
                            style={{
                                padding: "10px 14px",
                                border: "none",
                                borderRadius: "8px",
                                backgroundColor: "#7f1d1d",
                                color: "white",
                                fontWeight: 700,
                                cursor: loading ? "not-allowed" : "pointer",
                            }}
                        >
                            {t("common.confirm")}
                        </button>
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={showCloseOptionsModal}
                onClose={() => setShowCloseOptionsModal(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {t("posPage.closeOptionsModal.title")}
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: "0.85rem", color: "#6b7280", mb: 2 }}
                    >
                        {t("posPage.closeOptionsModal.description")}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        <button
                            onClick={() => setShowCloseOptionsModal(false)}
                            style={{
                                width: "100%",
                                padding: "12px",
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                backgroundColor: "white",
                                color: "#374151",
                                fontWeight: 600,
                                cursor: "pointer",
                            }}
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            onClick={() => {
                                setShowCloseOptionsModal(false)
                                setShowManualCloseModal(true)
                            }}
                            style={{
                                width: "100%",
                                padding: "12px",
                                border: "none",
                                borderRadius: "8px",
                                backgroundColor: "#7f1d1d",
                                color: "white",
                                fontWeight: 700,
                                cursor: "pointer",
                            }}
                        >
                            {t("posPage.closeOptionsModal.manualCount")}
                        </button>
                        <button
                            onClick={handleCloseTheoretical}
                            style={{
                                width: "100%",
                                padding: "12px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                backgroundColor: "#f9fafb",
                                color: "#374151",
                                fontWeight: 700,
                                cursor: "pointer",
                            }}
                        >
                            {t("posPage.closeOptionsModal.theoreticalFund")}:{" "}
                            {formatMinor(availableFundMinor)}
                        </button>
                    </Box>
                </DialogContent>
            </Dialog>

            <CashFundCountDialog
                open={showManualCloseModal}
                theoreticalMinor={availableFundMinor}
                chequeTheoreticalMinor={availableChequeMinor}
                chequeTheoreticalCount={Math.max(0, availableChequeCount)}
                currency="EUR"
                loading={loading}
                onCancel={() => setShowManualCloseModal(false)}
                onConfirm={(countedMinor) => handleCloseManual(countedMinor)}
            />

            {/* ═══ SUCCESS SNACKBAR ═══ */}
            <Snackbar
                open={!!successMsg}
                autoHideDuration={3000}
                onClose={() => setSuccessMsg(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    severity="success"
                    onClose={() => setSuccessMsg(null)}
                    sx={{ fontWeight: 600 }}
                >
                    {successMsg}
                </Alert>
            </Snackbar>

            <Dialog
                open={showOpenSessionModal && canSelectRegister}
                onClose={() => setShowOpenSessionModal(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {t("posPage.openSessionModal.title")}
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: "0.85rem", color: "#6b7280", mb: 1.5 }}
                    >
                        {t("posPage.openSessionModal.description", {
                            registerName,
                        })}
                    </Typography>
                    <TextField
                        fullWidth
                        type="number"
                        value={openingCountedInput}
                        onChange={(e) => setOpeningCountedInput(e.target.value)}
                        inputProps={{ min: 0, step: "0.01" }}
                        label={String(
                            t("posPage.openSessionModal.countedFundLabel")
                        )}
                        sx={{ mb: 2 }}
                        autoFocus
                    />
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                        }}
                    >
                        <button
                            onClick={() => setShowOpenSessionModal(false)}
                            style={{
                                padding: "10px 14px",
                                border: "1px solid #d1d5db",
                                borderRadius: "8px",
                                backgroundColor: "white",
                                color: "#374151",
                                fontWeight: 600,
                                cursor: "pointer",
                            }}
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            onClick={handleOpenSession}
                            disabled={loading}
                            style={{
                                padding: "10px 14px",
                                border: "none",
                                borderRadius: "8px",
                                backgroundColor: "#7f1d1d",
                                color: "white",
                                fontWeight: 700,
                                cursor: loading ? "not-allowed" : "pointer",
                            }}
                        >
                            {t("posPage.openSessionModal.open")}
                        </button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Inactivity auto-close dialog */}
            <Dialog
                open={showInactivityDialog}
                disableEscapeKeyDown
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, color: "#b71c1c" }}>
                    {t("posPage.inactivityModal.title")}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {t("posPage.inactivityModal.autoClosed")}
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        {t("posPage.inactivityModal.reportGenerated")}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setShowInactivityDialog(false)
                            redirectToRegisterSelection({
                                selectedCompanyId: companyId || undefined,
                                selectedOutletId: outletId || undefined,
                                selectedRegisterId: registerId || undefined,
                            })
                        }}
                        sx={{
                            textTransform: "none",
                            bgcolor: "#b71c1c",
                            "&:hover": { bgcolor: "#7f1d1d" },
                        }}
                    >
                        {t("common.understood")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default PosPage
